<template>
  <v-card class="plan-produccion-card" elevation="0">
    <div class="plan-produccion" :style="rootStyle">
      <div class="filters-bar">
        <datePicker v-model="filters.inicio" format="YYYY-MM-DD" label="Inicio" />
        <datePicker v-model="filters.fin" format="YYYY-MM-DD" label="Fin" />
        <v-autocomplete
          v-model="selectedMachineKeys"
          :items="currentMachineOptions"
          item-text="text"
          item-value="value"
          label="Máquina"
          multiple
          clearable
        />
        <v-btn color="primary" class="filter-btn" @click="init">
          Buscar
        </v-btn>
        <v-btn
          color="success"
          class="filter-btn"
          :loading="downloadingExcel"
          :disabled="downloadingExcel"
          @click="downloadExcel"
        >
          Descargar Excel
        </v-btn>
      </div>
      <div class="view-switch">
        <button
          type="button"
          class="view-toggle-btn"
          :class="{ 'view-toggle-btn--active': selectedPlanView === 'teorico' }"
          @click="selectedPlanView = 'teorico'"
        >
          Teorico
        </button>
        <button
          type="button"
          class="view-toggle-btn"
          :class="{ 'view-toggle-btn--active': selectedPlanView === 'real' }"
          @click="selectedPlanView = 'real'"
        >
          Real
        </button>
      </div>
      <div class="plan-scroll">
        <div class="plan-content">
          <div class="plan-header">
            <div class="days-row">
              <div class="label-spacer"></div>
              <div class="days-track-wrapper">
                <div class="days-track" :style="dayColumnsStyle">
                  <div v-for="day in dayMeta" :key="day.key" class="day-pill"
                    :class="{ active: day.key === selectedDayKey }" @click="selectedDayKey = day.key">
                    {{ day.label }}
                  </div>
                </div>
                <button class="nav-btn nav-left" type="button" aria-label="Dia anterior">
                  <v-icon small>mdi-chevron-left</v-icon>
                </button>
                <button class="nav-btn nav-right" type="button" aria-label="Dia siguiente">
                  <v-icon small>mdi-chevron-right</v-icon>
                </button>
              </div>
            </div>

            <div class="time-row">
              <div class="label-spacer"></div>
              <div class="time-labels" :style="dayColumnsStyle">
                <div v-for="day in dayMeta" :key="`hours-${day.key}`" class="time-day"
                  :class="{ active: day.key === selectedDayKey }">
                  <div class="time-day-labels" :style="hourGridStyle(day)">
                    <div v-for="(tick, idx) in day.hourTicks" :key="`tick-${day.key}-${idx}`" class="time-label">
                      {{ tick }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="legend-row">
              <div class="label-spacer"></div>
              <div class="legend-items">
                <div v-for="item in legend" :key="item.type" class="legend-item">
                  <span class="legend-dot" :class="`seg-${item.type}`"
                    :style="{ backgroundColor: typeColors[item.type] || '#cbd5e1' }"></span>
                  <span class="legend-text">{{ item.label }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="plan-body">
            <div v-for="machine in filteredMachines" :key="machine.key" class="machine-block">
              <div v-if="showMachineTitle" class="machine-title">
                {{ machine.name || "Sin maquina" }}
              </div>
              <div class="machine-rows">
                <div v-for="(row, idx) in machine.rows" :key="`row-${machine.key}-${idx}`" class="plan-row">
                  <div class="row-mold">
                    <div class="odt-detail">
                      <div><span class="odt-label">ODT:</span> {{ row.odt && row.odt.clave ? row.odt.clave : "N/D" }}
                      </div>
                      <div>
                        <span class="odt-label">Articulo:</span>
                        {{ row.odt && (row.odt.codigoArticulo || row.odt.articulo) ? (row.odt.codigoArticulo || row.odt.articulo) : "N/D" }}
                        <template v-if="selectedPlanView === 'real' && row.odt && row.odt.maquinaAsignada">
                          , <span class="odt-label">Maquina:</span> "{{ row.odt.maquinaAsignada }}"
                        </template>
                      </div>
                    </div>
                  </div>
                  <div class="row-timeline">
                    <div class="row-days" :style="dayColumnsStyle">
                      <div v-for="day in dayMeta" :key="`day-${machine.key}-${idx}-${day.key}`" class="row-day"
                        :class="{ active: day.key === selectedDayKey }" :style="dayGridStyle(day)">
                        <div class="segments">
                          <div v-for="(seg, sIdx) in segmentsByDay(row, day.key)"
                            :key="`seg-${machine.key}-${idx}-${day.key}-${sIdx}`" class="segment"
                            :class="segmentClasses(seg)" :style="segmentStyle(seg, day)" :title="segmentTitle(seg)"
                            @click="openDetalles(row, seg)">
                          </div>
                        </div>
                        <div v-if="showNowLine(day)" class="now-line" :style="nowStyle(day)"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <DialogDetallesOdt v-model="dialogOdt" :odt="selectedOdt" />
  </v-card>
</template>

<script>
import axios from "axios";
import DialogDetallesOdt from "./DialogDetallesOdt.vue";

export default {
  name: "PlanProduccion",
  components: {
    DialogDetallesOdt,
  },
  data: () => ({
    dialogOdt: false,
    selectedOdt: {},
    downloadingExcel: false,
    hourSlotPx: 70,
    moldColWidth: 280,
    selectedPlanView: "teorico",
    selectedDayKey: null,
    nowMarker: null,
    filters: {
      inicio: "",
      fin: "",
    },
    selectedMachineKeys: [],
    machineOptions: [],
    allMachines: [],
    realAllMachines: [],
    days: [],
    defaultWeeklyShifts: {
      1: [{ start: "08:00", end: "18:00", terminaDiaSiguiente: false }],
      2: [{ start: "07:00", end: "16:30", terminaDiaSiguiente: false }],
      3: [{ start: "07:00", end: "16:30", terminaDiaSiguiente: false }],
      4: [{ start: "07:00", end: "16:30", terminaDiaSiguiente: false }],
      5: [{ start: "07:00", end: "16:30", terminaDiaSiguiente: false }],
    },
    legend: [
      { label: "Fuera de horario", type: "fuera-horario" },
      { label: "Tiempo Extra", type: "tiempo-extra" },
      { label: "Paro", type: "paro-programado" },
      { label: "Produccion", type: "produccion" },
      { label: "Atraso", type: "retraso" },
      //{ label: "Muerto", type: "muerto" }
    ],
    machines: [],
    realMachines: [],
    estadoBaseMap: {
      2: { label: "Paro", type: "paro-programado" },
      3: { label: "Paro", type: "paro-programado" },
      4: { label: "Produccion", type: "produccion" },
      5: { label: "Atraso", type: "retraso" },
      //6: { label: "Muerto", type: "muerto" },
    },
    typeColors: {
      "fuera-horario": "#e53935",
      "tiempo-extra": "#1e88e5",
      programado: "#66bb6a",
      "paro-programado": "#616161",
      "paro-no-programado": "#263238",
      produccion: "#2e7d32",
      retraso: "#f7d238",
      //muerto: "#d9c3b1",
      retrabajo: "#8d4b30"
    }
  }),
  computed: {
    labelWidth() {
      return this.moldColWidth;
    },
    timelineWidthPx() {
      return this.dayMeta.reduce((total, day) => total + day.widthPx, 0);
    },
    dayColumnsStyle() {
      const columns = this.dayMeta.map((d) => `${d.widthPx}px`).join(" ");
      return {
        gridTemplateColumns: columns,
        width: `${this.timelineWidthPx}px`,
      };
    },
    rootStyle() {
      return {
        "--day-count": this.dayMeta.length,
        "--label-width": `${this.labelWidth}px`,
        "--timeline-width": `${this.timelineWidthPx}px`,
        "--mold-width": `${this.moldColWidth}px`,
      };
    },
    dayMeta() {
      return this.days.map((day) => {
        const ranges = Array.isArray(day.turnos) && day.turnos.length
          ? day.turnos
          : [{ start: "00:00", end: "23:59", terminaDiaSiguiente: false }];
        const times = ranges
          .map((r) => {
            const startMin = this.timeToMinutes(r.start || r.horarioInicio);
            let endMin = this.timeToMinutes(r.end || r.horarioFin);
            const endsNextDay = !!r.terminaDiaSiguiente;
            if (Number.isFinite(startMin) && Number.isFinite(endMin) && (endsNextDay || endMin <= startMin)) {
              endMin += 24 * 60;
            }
            return [startMin, endMin];
          })
          .filter((t) => Number.isFinite(t[0]) && Number.isFinite(t[1]));
        const startMin = times.length ? Math.min(...times.map((t) => t[0])) : 8 * 60;
        const endMin = times.length ? Math.max(...times.map((t) => t[1])) : 16 * 60;
        const totalMinRaw = Math.max(endMin - startMin, 60);
        const widthPx = (totalMinRaw / 60) * this.hourSlotPx;
        const hourTicks = this.buildHourTicks(startMin, endMin);
        const tickCount = Math.max(hourTicks.length, 1);
        return {
          ...day,
          startMin,
          endMin,
          totalMin: totalMinRaw,
          tickCount,
          hourTicks,
          widthPx,
        };
      });
    },
    dayMap() {
      const map = {};
      this.dayMeta.forEach((d) => {
        map[d.key] = d;
      });
      return map;
    },
    availableMachines() {
      return this.selectedPlanView === "real" ? this.realAllMachines : this.allMachines;
    },
    filteredMachines() {
      const selectedKeys = Array.isArray(this.selectedMachineKeys) ? this.selectedMachineKeys : [];
      if (!selectedKeys.length) return this.availableMachines;
      return this.availableMachines.filter((machine) => selectedKeys.includes(machine.key));
    },
    currentMachineOptions() {
      return this.availableMachines.map((machine) => ({
        text: machine.name,
        value: machine.key,
      }));
    },
    showMachineTitle() {
      return this.selectedPlanView !== "real";
    },
  },
  methods: {
    init() {
      this.ensureFilterRange();
      return axios
        .get("UnificadorPlanProduccion/GenerarPlanProduccionMitcenSemana", {
          params: {
            inicio: this.toApiDateTime(this.filters.inicio, false),
            fin: this.toApiDateTime(this.filters.fin, true),
            fechaConsulta: this.formatLocalDateTime(new Date()),
          },
        })
        .then((response) => {
          this.mapReportData(response && response.data ? response.data : {});
        })
        .catch((error) => {
          console.error("No se pudo cargar el plan de produccion semanal", error);
          this.days = [];
          this.machines = [];
          this.realMachines = [];
          this.allMachines = [];
          this.realAllMachines = [];
          this.selectedDayKey = null;
          this.nowMarker = null;
        });
    },
    ensureFilterRange() {
      const fallback = this.defaultWeekRange();
      if (!this.filters.inicio) this.filters.inicio = fallback.inicio;
      if (!this.filters.fin) this.filters.fin = fallback.fin;
      if (this.filters.inicio > this.filters.fin) {
        const swap = this.filters.inicio;
        this.filters.inicio = this.filters.fin;
        this.filters.fin = swap;
      }
    },
    buildExcelExportUrl() {
      this.ensureFilterRange();
      const params = new URLSearchParams({
        inicio: this.toApiDateTime(this.filters.inicio, false),
        fin: this.toApiDateTime(this.filters.fin, true),
        fechaConsulta: this.formatLocalDateTime(new Date()),
      });
      return `UnificadorPlanProduccion/ExportarPlanProduccionMitcenSemanaExcel?${params.toString()}`;
    },
    async downloadExcel() {
      this.downloadingExcel = true;
      try {
        const response = await axios.get(this.buildExcelExportUrl(), {
          responseType: "blob",
        });
        const fallbackStamp = this.formatLocalDateTime(new Date())
          .replace(/[-:T]/g, "")
          .slice(0, 15);
        this.saveExcelResponse(response, `PlanProduccionRealMitcen_${fallbackStamp}.xlsx`);
      } catch (error) {
        console.error("No se pudo descargar el excel del plan de produccion", error);
      } finally {
        this.downloadingExcel = false;
      }
    },
    saveExcelResponse(response, fallbackFileName) {
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = response && response.headers
        ? response.headers["content-disposition"] || response.headers["Content-Disposition"]
        : "";
      const fileNameMatch = typeof disposition === "string"
        ? disposition.match(/filename\*?=(?:UTF-8''|\"?)([^\";]+)/i)
        : null;
      const fileName = fileNameMatch && fileNameMatch[1]
        ? decodeURIComponent(fileNameMatch[1].replace(/\"/g, "").trim())
        : fallbackFileName;
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    defaultWeekRange() {
      const now = new Date();
      const day = now.getDay();
      const mondayOffset = day === 0 ? -6 : 1 - day;
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);
      start.setDate(start.getDate() + mondayOffset);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return {
        inicio: this.formatDateOnly(start),
        fin: this.formatDateOnly(end),
      };
    },
    formatDateOnly(dateValue) {
      const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
      if (Number.isNaN(date.getTime())) return "";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    toApiDateTime(dateValue, endOfDay = false) {
      const dateOnly = this.toDateKey(dateValue);
      if (!dateOnly) return null;
      return `${dateOnly}T${endOfDay ? "23:59:59" : "00:00:00"}`;
    },
    formatLocalDateTime(dateValue) {
      const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
      if (Number.isNaN(date.getTime())) return null;
      const hh = String(date.getHours()).padStart(2, "0");
      const mm = String(date.getMinutes()).padStart(2, "0");
      const ss = String(date.getSeconds()).padStart(2, "0");
      return `${this.formatDateOnly(date)}T${hh}:${mm}:${ss}`;
    },
    mapReportData(data) {
      const payload = data && typeof data === "object" ? data : {};
      const days = this.buildDays(payload);
      const odtDetailMap = this.buildOdtDetailMap(this.extractOdtSource(payload));
      this.allMachines = this.buildMachines(payload, odtDetailMap);
      this.realAllMachines = this.buildRealMachines(payload, odtDetailMap);
      const selectedKeys = Array.isArray(this.selectedMachineKeys) ? this.selectedMachineKeys : [];
      const validKeys = [
        ...this.allMachines.map((machine) => machine.key),
        ...this.realAllMachines.map((machine) => machine.key),
      ];
      this.selectedMachineKeys = selectedKeys
        .filter((key) => validKeys.includes(key));
      this.days = days;

      const todayKey = this.toDateKey(new Date());
      if (this.days.some((day) => day.key === todayKey)) {
        this.selectedDayKey = todayKey;
        const hh = String(new Date().getHours()).padStart(2, "0");
        const mm = String(new Date().getMinutes()).padStart(2, "0");
        this.nowMarker = { dayKey: todayKey, time: `${hh}:${mm}` };
      } else {
        if (!this.days.some((day) => day.key === this.selectedDayKey)) {
          this.selectedDayKey = this.days.length ? this.days[0].key : null;
        }
        this.nowMarker = null;
      }
    },
    extractRealBlocks(payload) {
      return this.pickFirstArray([
        payload && payload.ganttReal,
        payload && payload.ganttTiempoReal,
        payload && payload.gantt,
        payload && payload.dias,
        payload && payload.semana,
      ]);
    },
    extractOdtSource(payload) {
      return this.pickFirstArray([
        payload && payload.odts,
        payload && payload.ordenesTrabajo,
        payload && payload.ordenes,
      ]);
    },
    extractMachinesSource(payload) {
      return this.pickFirstArray([
        payload && payload.porMaquina,
        payload && payload.maquinas,
      ]);
    },
    extractHorarios(payload) {
      return this.pickFirstArray([
        payload && payload.horarios,
        payload && payload.turnos,
      ]);
    },
    extractRangeValue(payload, keys) {
      for (let i = 0; i < keys.length; i += 1) {
        const value = payload && payload[keys[i]];
        if (value) return value;
      }
      return "";
    },
    getConfiguredShiftsForWeekDay(weekDay) {
      const shifts = this.defaultWeeklyShifts && this.defaultWeeklyShifts[weekDay];
      return Array.isArray(shifts)
        ? shifts.map((shift) => this.normalizeShift(shift)).filter((shift) => shift)
        : [];
    },
    getShiftsForDate(value) {
      const dateKey = this.toDateKey(value);
      if (!dateKey) return [];
      const date = new Date(`${dateKey}T00:00:00`);
      if (Number.isNaN(date.getTime())) return [];
      const weekDay = date.getDay() === 0 ? 7 : date.getDay();
      return this.getConfiguredShiftsForWeekDay(weekDay);
    },
    buildDays(payload) {
      const byKey = {};
      const startKey = this.toDateKey(this.extractRangeValue(payload, ["inicioPeriodo", "inicio", "fechaInicio"]));
      const endKey = this.toDateKey(this.extractRangeValue(payload, ["finPeriodo", "fin", "fechaFin"]));
      if (startKey && endKey) {
        const start = new Date(`${startKey}T00:00:00`);
        const end = new Date(`${endKey}T00:00:00`);
        const globalHorarios = this.extractHorarios(payload);
        for (let cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
          const key = this.toDateKey(cursor);
          if (!byKey[key]) {
            byKey[key] = {
              key,
              label: this.buildDayLabel(key),
              turnos: [],
            };
          }
          if (!byKey[key].turnos.length) {
            const weekDay = cursor.getDay() === 0 ? 7 : cursor.getDay();
            const byWeekDay = globalHorarios.length
              ? globalHorarios
                .filter((h) => Number(h.diaSemanaNumero) === weekDay)
                .map((h) => this.normalizeShift(h))
                .filter((h) => h)
              : [];
            byKey[key].turnos = byWeekDay.length
              ? byWeekDay
              : [{ start: "00:00", end: "23:59", terminaDiaSiguiente: false }];
          }
        }
      }

      const days = Object.values(byKey).sort((a, b) => a.key.localeCompare(b.key));
      if (days.length) return days;
      const today = this.toDateKey(new Date());
      return [{
        key: today,
        label: this.buildDayLabel(today),
        turnos: [{ start: "00:00", end: "23:59", terminaDiaSiguiente: false }],
      }];
    },
    buildDayLabel(dayKey, diaSemana) {
      const dayName = (diaSemana || this.dayNameFromKey(dayKey)).toUpperCase();
      const dateLabel = this.formatDayLabelDate(dayKey);
      if (!dateLabel) return dayName;
      return `${dayName} ${dateLabel}`;
    },
    formatDayLabelDate(dayKey) {
      if (!dayKey) return "";
      const date = new Date(`${dayKey}T00:00:00`);
      if (Number.isNaN(date.getTime())) return dayKey;
      const dd = String(date.getDate()).padStart(2, "0");
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const yyyy = date.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    },
    applyMachineFilter() {
      const selectedKeys = Array.isArray(this.selectedMachineKeys) ? this.selectedMachineKeys : [];
      this.selectedMachineKeys = selectedKeys;
      if (!selectedKeys.length) {
        this.machines = this.allMachines.slice();
        return;
      }
      this.machines = this.allMachines
        .filter((machine) => selectedKeys.includes(machine.key));
    },
    buildMachines(payload, odtDetailMap) {
      const odts = this.extractOdtSource(payload);
      if (odts.length) {
        return this.buildMachinesFromOdtPayload(odts, odtDetailMap);
      }
      return [];
    },
    buildRealMachines(payload, odtDetailMap) {
      const odts = this.extractOdtSource(payload);
      if (odts.length) {
        return this.buildRealMachinesFromOdtPayload(odts, odtDetailMap);
      }
      return [];
    },
    buildMachinesFromOdtPayload(odts, odtDetailMap) {
      const machineKey = "TEORICO";
      const machines = {
        [machineKey]: {
          name: "Plan teorico",
          grouped: {},
        },
      };
      const source = Array.isArray(odts) ? odts : [];

      source.forEach((odt) => {
        const teorico = this.pickFirstArray([odt && odt.teorico]);
        const timelineOps = [];

        teorico.forEach((op) => {
          timelineOps.push({
            isReal: false,
            op: this.normalizeOdtTimelineOperation(odt, op, false),
          });
        });

        timelineOps.forEach((item) => {
          const op = item && item.op;
          if (!op) return;
          const rowKey = this.buildOdtGroupKey(odt);
          const opDetailKey = this.buildRowKey(op);
          const rowDetail = odtDetailMap && odtDetailMap[opDetailKey] ? odtDetailMap[opDetailKey] : null;
          const grouped = machines[machineKey].grouped;
          if (!grouped[rowKey]) {
            grouped[rowKey] = {
              key: rowKey,
              mold: this.buildOdtGroupLabel(odt, op),
              base: this.buildOdtGroupBase(odt, op),
              segments: [],
              detalleOps: [],
            };
          }

          const detalle = this.toDetalleOperacion(op, item.isReal, rowDetail);
          const segment = this.toSegment(op, this.toDateKey(op.inicio), item.isReal);
          if (segment) grouped[rowKey].segments.push(segment);
          grouped[rowKey].detalleOps.push(detalle);
        });
      });

      return Object.keys(machines)
        .map((machineKey) => {
          const machine = machines[machineKey];
            const rows = Object.values(machine.grouped)
              .map((group) => {
                group.segments.sort((a, b) => (a.day === b.day ? a.startMin - b.startMin : a.day.localeCompare(b.day)));
                group.detalleOps.sort((a, b) => String(a.inicio).localeCompare(String(b.inicio)));
                const mergedSegments = this.mergeConsecutiveFragments([], group.segments).segments;
                group.detalleOps = group.detalleOps.map((detail, idx) => ({
                  ...detail,
                  fragmentoNumero: idx + 1,
                }));
                group.segments = mergedSegments.map((segment, idx) => ({
                  ...segment,
                  fragmentoNumero: idx + 1,
                }));
                const aggregateSegments = this.buildOdtAggregateSegments(group.base, group.segments);
                if (aggregateSegments.length) {
                group.segments = aggregateSegments;
              }
              const odt = this.buildOdt(group.base, group.detalleOps, null);
              group.segments.forEach((seg) => { seg.odt = odt; });
              return {
                key: group.key,
                mold: group.mold,
                odt,
                segments: group.segments,
              };
            })
            .sort((a, b) => a.mold.localeCompare(b.mold));

          return {
            key: machineKey,
            name: machine.name,
            rows,
          };
        })
        .filter((machine) => machine && machine.name && machine.rows.length)
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    buildRealMachinesFromOdtPayload(odts, odtDetailMap) {
      const machineKey = "REAL";
      const machines = {
        [machineKey]: {
          name: "Plan real",
          grouped: {},
        },
      };
      const source = Array.isArray(odts) ? odts : [];

      source.forEach((odt) => {
        const real = this.pickFirstArray([odt && odt.real]);
        real.forEach((rawOp) => {
          const op = this.normalizeOdtTimelineOperation(odt, rawOp, true);
          if (!op) return;
          const rowKey = this.buildRealOdtGroupKey(odt, op);
          const opDetailKey = this.buildRowKey(op);
          const rowDetail = odtDetailMap && odtDetailMap[opDetailKey] ? odtDetailMap[opDetailKey] : null;
          const grouped = machines[machineKey].grouped;
          if (!grouped[rowKey]) {
            grouped[rowKey] = {
              key: rowKey,
              mold: this.buildRealOdtGroupLabel(odt, op),
              base: this.buildRealOdtGroupBase(odt, op),
              segments: [],
              detalleOps: [],
            };
          }
          const detalle = this.toDetalleOperacion(op, true, rowDetail);
          const segment = this.toSegment(op, this.toDateKey(op.inicio), true);
          if (segment) grouped[rowKey].segments.push(segment);
          grouped[rowKey].detalleOps.push(detalle);
        });

        if (!real.length && this.hasRealDelaySummary(odt)) {
          const op = this.buildSyntheticRealDelayOperation(odt);
          if (op) {
            const rowKey = this.buildRealOdtGroupKey(odt, op);
            const grouped = machines[machineKey].grouped;
            if (!grouped[rowKey]) {
              grouped[rowKey] = {
                key: rowKey,
                mold: this.buildRealOdtGroupLabel(odt, op),
                base: this.buildRealOdtGroupBase(odt, op),
                segments: [],
                detalleOps: [],
              };
            }
            const detalle = this.toDetalleOperacion(op, true, null);
            const segment = this.toSegment(op, this.toDateKey(op.inicio), true);
            if (segment) grouped[rowKey].segments.push(segment);
            grouped[rowKey].detalleOps.push(detalle);
          }
        }
      });

      return Object.keys(machines)
        .map((currentKey) => {
          const machine = machines[currentKey];
          const rows = Object.values(machine.grouped)
            .map((group) => {
              group.segments.sort((a, b) => (a.day === b.day ? a.startMin - b.startMin : a.day.localeCompare(b.day)));
              group.detalleOps.sort((a, b) => String(a.inicio).localeCompare(String(b.inicio)));
              const aggregateSegments = this.buildRealAggregateSegments(group.segments);
              const odt = this.buildOdt(group.base, group.detalleOps, null);
              aggregateSegments.forEach((seg) => { seg.odt = odt; });
              return {
                key: group.key,
                mold: group.mold,
                odt,
                segments: aggregateSegments,
              };
            })
            .filter((row) => row.segments.length)
            .sort((a, b) => a.mold.localeCompare(b.mold));

          return {
            key: currentKey,
            name: machine.name,
            rows,
          };
        })
        .filter((machine) => machine && machine.name && machine.rows.length)
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    hasRealDelaySummary(odt) {
      const atrasoInicio = this.pickFirstNumber([
        odt && odt.atrasoInicio,
        odt && odt.retrasoParosSegundos,
      ]);
      const teorico = this.pickFirstArray([odt && odt.teorico]);
      const firstTeorico = teorico[0] || {};
      const inicioTeorico = this.pickFirstText([
        firstTeorico && firstTeorico.inicioTeorico,
        odt && odt.fechaInicioProduccion,
      ]);
      return Number.isFinite(atrasoInicio) && atrasoInicio > 0 && !!inicioTeorico;
    },
    buildSyntheticRealDelayOperation(odt) {
      const atrasoInicio = this.pickFirstNumber([
        odt && odt.atrasoInicio,
        odt && odt.retrasoParosSegundos,
      ]);
      const teorico = this.pickFirstArray([odt && odt.teorico]);
      const firstTeorico = teorico[0] || {};
      const inicioTeorico = this.pickFirstText([
        firstTeorico && firstTeorico.inicioTeorico,
        odt && odt.fechaInicioProduccion,
      ]);
      const finTeorico = this.pickFirstText([
        firstTeorico && firstTeorico.finTeorico,
        odt && odt.fechaFinTeorica,
      ]);
      if (!Number.isFinite(atrasoInicio) || atrasoInicio <= 0 || !inicioTeorico) {
        return null;
      }
      const inicioBase = new Date(inicioTeorico);
      if (Number.isNaN(inicioBase.getTime())) return null;
      const finAtraso = new Date(inicioBase.getTime() + (atrasoInicio * 1000));
      return {
        idTrabajoOrden: this.pickFirstNumber([odt && odt.idTrabajoOrden]) || 0,
        idTrabajoOrdenDetalle: this.pickFirstNumber([firstTeorico && firstTeorico.idTrabajoOrdenDetalle]) || 0,
        idOperacion: this.pickFirstNumber([firstTeorico && firstTeorico.idOperacion]) || 0,
        idOperacionReal: null,
        idPaso: this.pickFirstNumber([firstTeorico && firstTeorico.idPaso]),
        numPaso: this.pickFirstNumber([firstTeorico && firstTeorico.numPaso]),
        claveTrabajoOrden: this.pickFirstText([odt && odt.claveODT, odt && odt.claveTrabajoOrden]),
        articulo: this.pickFirstText([firstTeorico && firstTeorico.articulo]),
        codigoArticulo: this.pickFirstText([firstTeorico && firstTeorico.codigoArticulo, firstTeorico && firstTeorico.articulo]),
        cliente: this.pickFirstText([odt && odt.cliente]),
        prioridad: this.pickFirstNumber([odt && odt.prioridad]) || 0,
        area: this.pickFirstText([firstTeorico && firstTeorico.area]),
        receta: this.pickFirstText([firstTeorico && firstTeorico.area]),
        inicio: inicioTeorico,
        fin: finAtraso.toISOString(),
        inicioReal: "",
        finReal: "",
        duracionSeg: atrasoInicio,
        estadoId: 5,
        estado: "Atraso",
        esTeorica: false,
        nombreMaquina: "",
        maquina: "",
        maquinasPosibles: [],
        maquinasInstanciaPosibles: [],
        maquinasAsignadas: [],
        ingredientes: [],
        piezasPlaneadas: this.pickFirstNumber([firstTeorico && firstTeorico.piezasPlaneadas]),
        piezasReportadas: [],
        piezasReportadasTotal: 0,
        tiempoCicloSegundos: this.pickFirstNumber([firstTeorico && firstTeorico.tiempoCicloSegundos]),
        tiempoExtraSegundos: 0,
        tieneAsignacionReal: false,
        inicioTeoricoRef: inicioTeorico,
        finTeoricoRef: finTeorico,
        isSyntheticDelaySummary: true,
        subpasos: [],
        subpasosPendientes: [],
        subpasosEnProceso: [],
        subpasosCumplidos: [],
        operarios: [],
        paros: [],
      };
    },
    buildRealOdtGroupKey(odt, op) {
      const odtKey = this.buildOdtGroupKey(odt);
      const machineKey = this.resolveExplicitRealMachine(op);
      return `${odtKey}|${machineKey}`;
    },
    buildRealOdtGroupLabel(odt, op) {
      const clave = this.pickFirstText([
        odt && odt.claveODT,
        odt && odt.claveTrabajoOrden,
        op && op.claveTrabajoOrden,
      ]) || "N/D";
      const articulo = this.pickFirstText([
        op && op.codigoArticulo,
        op && op.articulo,
      ]) || "N/D";
      const machine = this.resolveExplicitRealMachine(op, "");
      return machine
        ? `ODT: ${clave} | Articulo: ${articulo} (${machine})`
        : `ODT: ${clave} | Articulo: ${articulo}`;
    },
    buildRealOdtGroupBase(odt, op) {
      return {
        ...this.buildOdtGroupBase(odt, op),
        maquinaAsignada: this.resolveExplicitRealMachine(op, ""),
      };
    },
    resolveExplicitRealMachine(op, fallback = "SIN_MAQUINA") {
      const machine = this.pickFirstText([
        this.firstMachineName(op && op.maquinasAsignadas),
        op && op.maquinaAsignada,
        op && op.maquina,
        op && op.nombreMaquina,
      ]);
      return machine || fallback;
    },
    buildOdtGroupKey(odt) {
      const idTrabajoOrden = this.pickFirstNumber([odt && odt.idTrabajoOrden]);
      if (idTrabajoOrden !== null) return `OT-${idTrabajoOrden}`;
      const clave = this.pickFirstText([odt && odt.claveODT, odt && odt.claveTrabajoOrden]);
      return clave || "OT-SIN_CLAVE";
    },
    buildOdtGroupLabel(odt, op) {
      const clave = this.pickFirstText([
        odt && odt.claveODT,
        odt && odt.claveTrabajoOrden,
        op && op.claveTrabajoOrden,
      ]) || "N/D";
      const articulo = this.pickFirstText([
        op && op.codigoArticulo,
        op && op.articulo,
      ]) || "N/D";
      return `ODT: ${clave} | Articulo: ${articulo}`;
    },
    buildOdtGroupBase(odt, op) {
      return {
        idTrabajoOrden: this.pickFirstNumber([odt && odt.idTrabajoOrden]) || 0,
        claveTrabajoOrden: this.pickFirstText([
          odt && odt.claveODT,
          odt && odt.claveTrabajoOrden,
          op && op.claveTrabajoOrden,
        ]),
        claveODT: this.pickFirstText([
          odt && odt.claveODT,
          odt && odt.claveTrabajoOrden,
        ]),
        articulo: this.pickFirstText([op && op.articulo]),
        codigoArticulo: this.pickFirstText([op && op.codigoArticulo, op && op.articulo]),
        cliente: this.pickFirstText([odt && odt.cliente]),
        prioridad: this.pickFirstNumber([odt && odt.prioridad]) || 0,
        fechaInicioProduccion: this.pickFirstText([odt && odt.fechaInicioProduccion]),
        fechaEntrega: this.pickFirstText([odt && odt.fechaEntrega]),
        fechaFinTeorica: this.pickFirstText([odt && odt.fechaFinTeorica]),
        maquinasDisponibles: this.pickFirstArray([op && op.maquinasPosibles]),
      };
    },
    buildOdtAggregateSegments(base, segments) {
      const inicio = this.pickFirstText([base && base.fechaInicioProduccion]);
      const fin = this.pickFirstText([base && base.fechaEntrega]);
      const finTooltip = this.pickFirstText([base && base.fechaFinTeorica, fin]);
      if (!inicio || !fin) return [];

      const source = Array.isArray(segments) ? segments : [];
      const first = source[0] || {};
      const articulo = this.mergeDistinctLabels(...source.map((item) => item && item.articulo));
      const codigoArticulo = this.mergeDistinctLabels(...source.map((item) => item && item.codigoArticulo));
      const area = this.mergeDistinctLabels(...source.map((item) => item && item.area));
      const maquinaAsignada = this.mergeDistinctLabels(...source.map((item) => item && item.maquinaAsignada));
      const piezasPlaneadasTexto = this.mergeDistinctLabels(...source.map((item) => item && item.piezasPlaneadasTexto));
      const plannedSegments = this.buildScheduledSegmentsForRange(inicio, fin, (segment) => ({
        ...first,
        ...segment,
        articulo,
        codigoArticulo,
        area,
        maquinaAsignada,
        piezasPlaneadasTexto,
        inicioBarraTexto: this.formatTooltipDateTime(inicio),
        finBarraTexto: this.formatTooltipDateTime(fin),
        finTexto: this.formatTooltipDateTime(finTooltip),
      }));
      const outsideSegments = this.buildTheoreticalBoundaryOutsideSegments(inicio, fin, (segment) => ({
        ...first,
        ...segment,
        type: "fuera-horario",
        estado: "Fuera de horario",
        isOutsideSchedule: true,
        articulo,
        codigoArticulo,
        area,
        maquinaAsignada,
        piezasPlaneadasTexto,
        inicioBarraTexto: this.formatTooltipDateTime(inicio),
        finBarraTexto: this.formatTooltipDateTime(fin),
        finTexto: this.formatTooltipDateTime(finTooltip),
      }));
      return [...plannedSegments, ...outsideSegments]
        .sort((a, b) => (a.day === b.day ? a.startMin - b.startMin : a.day.localeCompare(b.day)));
    },
    buildRealAggregateSegments(segments) {
      const source = Array.isArray(segments) ? segments : [];
      if (!source.length) return [];

      const sorted = source
        .filter((item) => item && (item.fullStart || (item.day && item.start)) && (item.fullEnd || (item.day && item.end)))
        .slice()
        .sort((a, b) => {
          const left = a.fullStart || `${a.day}T${a.start}`;
          const right = b.fullStart || `${b.day}T${b.start}`;
          return left.localeCompare(right);
        });
      if (!sorted.length) return [];

      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      const startDateTime = first.fullStart || `${first.day}T${first.start}`;
      const endDateTime = last.fullEnd || `${last.day}T${last.end}`;
      const startDate = new Date(startDateTime);
      const endDate = new Date(endDateTime);
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) || endDate < startDate) {
        return [];
      }

      const articulo = this.mergeDistinctLabels(...sorted.map((item) => item && item.articulo));
      const codigoArticulo = this.mergeDistinctLabels(...sorted.map((item) => item && item.codigoArticulo));
      const area = this.mergeDistinctLabels(...sorted.map((item) => item && item.area));
      const maquinaAsignada = this.mergeDistinctLabels(...sorted.map((item) => item && item.maquinaAsignada));
      const piezasPlaneadasTexto = this.mergeDistinctLabels(...sorted.map((item) => item && item.piezasPlaneadasTexto));
      const piezasReportadasTexto = this.mergeDistinctLabels(...sorted.map((item) => item && item.piezasReportadasTexto));
      const delaySegments = this.buildRealDelaySegments(sorted);
      const onlySyntheticDelay = sorted.length > 0 && sorted.every((item) =>
        item && item.isSyntheticDelaySummary === true && !item.idOperacionReal && Number(item.estadoId) === 5
      );
      if (onlySyntheticDelay && delaySegments.length) {
        return delaySegments
          .sort((a, b) => (a.day === b.day ? a.startMin - b.startMin : a.day.localeCompare(b.day)));
      }
      const segmentsByDay = this.buildScheduledSegmentsForRange(startDateTime, endDateTime, (segment) => ({
        ...first,
        ...segment,
        planView: "real",
        type: first && first.isSyntheticDelaySummary ? "retraso" : "programado",
        estado: first && first.isSyntheticDelaySummary ? "Atraso" : "Produccion",
        articulo,
        codigoArticulo,
        area,
        maquinaAsignada,
        piezasPlaneadasTexto,
        piezasReportadasTexto,
      }));
      const extraSegments = this.buildRealExtraSegments(sorted);
      const paroSegments = this.buildRealParoSegments(sorted);
      return [...delaySegments, ...segmentsByDay, ...extraSegments, ...paroSegments]
        .sort((a, b) => (a.day === b.day ? a.startMin - b.startMin : a.day.localeCompare(b.day)));
    },
    buildRealDelaySegments(segments) {
      const source = Array.isArray(segments) ? segments : [];
      const result = [];

      source.forEach((segment) => {
        const teoricoInicio = this.pickFirstText([segment && segment.inicioTeoricoRef]);
        const teoricoFin = this.pickFirstText([segment && segment.finTeoricoRef]);
        const realInicio = this.pickFirstText([segment && segment.fullStart]);
        const realFin = this.pickFirstText([segment && segment.fullEnd]);

        result.push(...this.buildDelaySlices(teoricoInicio, realInicio, segment));
        result.push(...this.buildDelaySlices(teoricoFin, realFin, segment));
      });

      return result;
    },
    buildDelaySlices(expectedValue, actualValue, segment) {
      const expected = new Date(expectedValue);
      const actual = segment && segment.isSyntheticDelaySummary
        ? this.resolveSyntheticDelayActualDate(actualValue, expectedValue)
        : new Date(actualValue);
      if (Number.isNaN(expected.getTime()) || Number.isNaN(actual.getTime()) || actual <= expected) {
        return [];
      }

      const durationSeconds = Math.round((actual.getTime() - expected.getTime()) / 1000);
      return this.buildScheduledSegmentsForRange(expectedValue, actualValue, (slice) => ({
        ...segment,
        ...slice,
        planView: "real",
        type: "retraso",
        isDelay: true,
        estado: "Atraso",
        delayDurationSeg: durationSeconds,
        delayDurationTexto: this.formatDurationHoursMinutes(durationSeconds),
      }));
    },
    resolveSyntheticDelayActualDate(actualValue, expectedValue) {
      const expected = new Date(expectedValue);
      const actual = new Date(actualValue);
      const now = new Date();
      if (Number.isNaN(expected.getTime())) return actual;
      const capped = Number.isNaN(actual.getTime()) || actual > now ? now : actual;
      return capped > expected ? capped : expected;
    },
    buildRealExtraSegments(segments) {
      const source = Array.isArray(segments) ? segments : [];
      const result = [];

      source.forEach((segment) => {
        const inicio = this.pickFirstText([segment && segment.fullStart]);
        const fin = this.pickFirstText([segment && segment.fullEnd]);
        const tiempoExtraSegundos = this.pickFirstNumber([segment && segment.tiempoExtraSegundos]);
        if (!inicio || !fin || !Number.isFinite(tiempoExtraSegundos) || tiempoExtraSegundos <= 0) return;

        const extraSlices = this.buildRealBoundaryOutsideSegments(inicio, fin, (slice) => ({
          ...segment,
          ...slice,
          planView: "real",
          type: "tiempo-extra",
          isExtraTime: true,
          estado: "Tiempo Extra",
          tiempoExtraTexto: this.formatDurationHoursMinutes(tiempoExtraSegundos),
        }));
        result.push(...extraSlices);
      });

      return result;
    },
    buildRealParoSegments(segments) {
      const source = Array.isArray(segments) ? segments : [];
      const result = [];

      source.forEach((segment) => {
        const paros = Array.isArray(segment && segment.paros) ? segment.paros : [];
        paros.forEach((paro) => {
          const inicio = this.pickFirstText([paro && paro.inicio]);
          const fin = this.pickFirstText([paro && paro.fin]);
          if (!inicio || !fin) return;
          const paroSlices = this.buildScheduledSegmentsForRange(inicio, fin, (slice) => ({
            ...segment,
            ...slice,
            planView: "real",
            type: "paro-programado",
            isParo: true,
            conceptoParo: this.pickFirstText([paro && paro.concepto]),
            duracionParoSeg: this.pickFirstNumber([paro && paro.duracionSegundos]) || this.diffSeconds(inicio, fin),
            piezasNoOkParo: this.pickFirstNumber([paro && paro.cantidadNoOk]),
            idParo: this.pickFirstNumber([paro && paro.idParo]),
          }));
          result.push(...paroSlices);
        });
      });

      return result;
    },
    buildScheduledSegmentsForRange(startDateTime, endDateTime, buildSegment) {
      const startDate = new Date(startDateTime);
      const endDate = new Date(endDateTime);
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) || endDate < startDate) {
        return [];
      }

      const segments = [];
      const cursor = new Date(startDate);
      cursor.setHours(0, 0, 0, 0);
      while (cursor <= endDate) {
        const dayKey = this.toDateKey(cursor);
        const shifts = this.getShiftsForDate(dayKey);
        shifts.forEach((shift) => {
          const overlap = this.getShiftOverlap(startDateTime, endDateTime, dayKey, shift);
          if (!overlap) return;
          segments.push(buildSegment(overlap));
        });
        cursor.setDate(cursor.getDate() + 1);
      }
      return segments;
    },
    buildRealBoundaryOutsideSegments(startValue, endValue, buildSegment) {
      const startDate = startValue instanceof Date ? new Date(startValue) : new Date(startValue);
      const endDate = endValue instanceof Date ? new Date(endValue) : new Date(endValue);
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) || endDate <= startDate) {
        return [];
      }

      const segments = [];
      const cursor = new Date(startDate);
      cursor.setHours(0, 0, 0, 0);
      while (cursor <= endDate) {
        const dayKey = this.toDateKey(cursor);
        if (!dayKey) break;
        const dayStart = new Date(`${dayKey}T00:00:00`);
        const dayEnd = new Date(`${dayKey}T23:59:59`);
        const sliceStart = new Date(Math.max(startDate.getTime(), dayStart.getTime()));
        const sliceEnd = new Date(Math.min(endDate.getTime(), dayEnd.getTime()));
        if (sliceEnd > sliceStart) {
          const shifts = this.getShiftsForDate(dayKey)
            .map((shift) => this.getShiftDateRange(dayKey, shift))
            .filter((shift) => shift)
            .sort((a, b) => a.start.getTime() - b.start.getTime());

          if (!shifts.length) {
            const wholeSegment = this.buildDateRangeSegment(sliceStart, sliceEnd, dayKey);
            if (wholeSegment) segments.push(buildSegment(wholeSegment));
          } else {
            const firstShift = shifts[0];
            const lastShift = shifts[shifts.length - 1];
            if (sliceStart < firstShift.start) {
              const beforeSegment = this.buildDateRangeSegment(
                sliceStart,
                new Date(Math.min(sliceEnd.getTime(), firstShift.start.getTime())),
                dayKey
              );
              if (beforeSegment) segments.push(buildSegment(beforeSegment));
            }
            if (sliceEnd > lastShift.end) {
              const afterSegment = this.buildDateRangeSegment(
                new Date(Math.max(sliceStart.getTime(), lastShift.end.getTime())),
                sliceEnd,
                dayKey
              );
              if (afterSegment) segments.push(buildSegment(afterSegment));
            }
          }
        }
        cursor.setDate(cursor.getDate() + 1);
      }
      return segments;
    },
    buildTheoreticalBoundaryOutsideSegments(startDateTime, endDateTime, buildSegment) {
      const startDate = new Date(startDateTime);
      const endDate = new Date(endDateTime);
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) || endDate < startDate) {
        return [];
      }

      const segments = [];
      const startOutside = this.getSameDayOutsideBoundarySegment(startDate, "start");
      if (startOutside) {
        segments.push(...this.buildCalendarSegmentsForRange(startOutside.start, startOutside.end, buildSegment));
      }
      const endOutside = this.getSameDayOutsideBoundarySegment(endDate, "end");
      if (endOutside) {
        segments.push(...this.buildCalendarSegmentsForRange(endOutside.start, endOutside.end, buildSegment));
      }
      return segments;
    },
    buildCalendarSegmentsForRange(startValue, endValue, buildSegment) {
      const startDate = startValue instanceof Date ? new Date(startValue) : new Date(startValue);
      const endDate = endValue instanceof Date ? new Date(endValue) : new Date(endValue);
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) || endDate <= startDate) {
        return [];
      }

      const segments = [];
      const cursor = new Date(startDate);
      cursor.setHours(0, 0, 0, 0);
      while (cursor <= endDate) {
        const dayKey = this.toDateKey(cursor);
        if (!dayKey) continue;
        const dayStart = new Date(`${dayKey}T00:00:00`);
        const dayEnd = new Date(`${dayKey}T23:59:59`);
        const sliceStart = new Date(Math.max(startDate.getTime(), dayStart.getTime()));
        const sliceEnd = new Date(Math.min(endDate.getTime(), dayEnd.getTime()));
        if (sliceEnd > sliceStart) {
          const segment = this.buildDateRangeSegment(sliceStart, sliceEnd, dayKey);
          if (segment) segments.push(buildSegment(segment));
        }
        cursor.setDate(cursor.getDate() + 1);
      }
      return segments;
    },
    isDateTimeWithinShifts(value) {
      const target = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(target.getTime())) return false;
      const dayKey = this.toDateKey(target);
      const shifts = this.getShiftsForDate(dayKey);
      return shifts.some((shift) => {
        const range = this.getShiftDateRange(dayKey, shift);
        return range && target >= range.start && target <= range.end;
      });
    },
    getSameDayOutsideBoundarySegment(value, mode) {
      const target = value instanceof Date ? new Date(value) : new Date(value);
      if (Number.isNaN(target.getTime()) || this.isDateTimeWithinShifts(target)) return null;
      const dayKey = this.toDateKey(target);
      if (!dayKey) return null;
      const dayStart = new Date(`${dayKey}T00:00:00`);
      const dayEnd = new Date(`${dayKey}T23:59:59`);
      const shifts = this.getShiftsForDate(dayKey)
        .map((shift) => this.getShiftDateRange(dayKey, shift))
        .filter((shift) => shift)
        .sort((a, b) => a.start.getTime() - b.start.getTime());

      if (!shifts.length) {
        return mode === "start"
          ? { start: target, end: dayEnd }
          : { start: dayStart, end: target };
      }

      if (mode === "start") {
        for (let i = 0; i < shifts.length; i += 1) {
          if (target < shifts[i].start) {
            return { start: target, end: shifts[i].start };
          }
        }
        return { start: target, end: dayEnd };
      }

      for (let i = shifts.length - 1; i >= 0; i -= 1) {
        if (target > shifts[i].end) {
          return { start: shifts[i].end, end: target };
        }
      }
      return { start: dayStart, end: target };
    },
    findNextWorkingDateTime(value) {
      const target = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(target.getTime())) return null;
      for (let offset = 0; offset <= 14; offset += 1) {
        const cursor = new Date(target);
        cursor.setDate(cursor.getDate() + offset);
        const dayKey = this.toDateKey(cursor);
        const shifts = this.getShiftsForDate(dayKey)
          .map((shift) => this.getShiftDateRange(dayKey, shift))
          .filter((shift) => shift)
          .sort((a, b) => a.start.getTime() - b.start.getTime());
        for (let i = 0; i < shifts.length; i += 1) {
          if (shifts[i].start >= target) return shifts[i].start;
          if (target >= shifts[i].start && target <= shifts[i].end) return target;
        }
      }
      return null;
    },
    findPreviousWorkingDateTime(value) {
      const target = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(target.getTime())) return null;
      for (let offset = 0; offset <= 14; offset += 1) {
        const cursor = new Date(target);
        cursor.setDate(cursor.getDate() - offset);
        const dayKey = this.toDateKey(cursor);
        const shifts = this.getShiftsForDate(dayKey)
          .map((shift) => this.getShiftDateRange(dayKey, shift))
          .filter((shift) => shift)
          .sort((a, b) => a.start.getTime() - b.start.getTime());
        for (let i = shifts.length - 1; i >= 0; i -= 1) {
          if (shifts[i].end <= target) return shifts[i].end;
          if (target >= shifts[i].start && target <= shifts[i].end) return target;
        }
      }
      return null;
    },
    getShiftDateRange(dayKey, shift) {
      if (!dayKey || !shift) return null;
      const start = new Date(`${dayKey}T${shift.start}:00`);
      const end = new Date(`${dayKey}T${shift.end}:00`);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
      if (shift.terminaDiaSiguiente || end <= start) {
        end.setDate(end.getDate() + 1);
      }
      return { start, end };
    },
    buildDateRangeSegment(startDate, endDate, dayKey) {
      if (!(startDate instanceof Date) || !(endDate instanceof Date)) return null;
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) || endDate <= startDate) return null;
      const start = `${String(startDate.getHours()).padStart(2, "0")}:${String(startDate.getMinutes()).padStart(2, "0")}:${String(startDate.getSeconds()).padStart(2, "0")}`;
      const end = `${String(endDate.getHours()).padStart(2, "0")}:${String(endDate.getMinutes()).padStart(2, "0")}:${String(endDate.getSeconds()).padStart(2, "0")}`;
      const startMin = this.timeToMinutes(start);
      let endMin = this.timeToMinutes(end);
      if (Number.isFinite(startMin) && Number.isFinite(endMin) && endMin < startMin) {
        endMin += 24 * 60;
      }
      return {
        day: dayKey,
        start,
        end,
        startMin,
        endMin,
      };
    },
    getShiftOverlap(rangeStartValue, rangeEndValue, dayKey, shift) {
      if (!dayKey || !shift) return null;
      const rangeStart = new Date(rangeStartValue);
      const rangeEnd = new Date(rangeEndValue);
      const shiftStart = new Date(`${dayKey}T${shift.start}:00`);
      const shiftEnd = new Date(`${dayKey}T${shift.end}:00`);
      if (shift.terminaDiaSiguiente || shiftEnd <= shiftStart) {
        shiftEnd.setDate(shiftEnd.getDate() + 1);
      }
      const overlapStart = new Date(Math.max(rangeStart.getTime(), shiftStart.getTime()));
      const overlapEnd = new Date(Math.min(rangeEnd.getTime(), shiftEnd.getTime()));
      if (overlapEnd <= overlapStart) return null;
      const start = `${String(overlapStart.getHours()).padStart(2, "0")}:${String(overlapStart.getMinutes()).padStart(2, "0")}:${String(overlapStart.getSeconds()).padStart(2, "0")}`;
      const end = `${String(overlapEnd.getHours()).padStart(2, "0")}:${String(overlapEnd.getMinutes()).padStart(2, "0")}:${String(overlapEnd.getSeconds()).padStart(2, "0")}`;
      const startMin = this.timeToMinutes(start);
      let endMin = this.timeToMinutes(end);
      if (Number.isFinite(startMin) && Number.isFinite(endMin) && endMin < startMin) {
        endMin += 24 * 60;
      }
      return {
        day: dayKey,
        start,
        end,
        startMin,
        endMin,
      };
    },
    normalizeOdtTimelineOperation(odt, op, isReal) {
      if (!op || typeof op !== "object") return null;
      const teoricoMatch = isReal ? this.findMatchingTheoreticalOperation(odt, op) : op;
      const inicio = this.pickFirstText([
        isReal ? op.inicio : "",
        isReal ? op.inicioReal : "",
        op.inicioTeorico,
      ]);
      const fin = this.pickFirstText([
        isReal ? op.fin : "",
        isReal ? op.finReal : "",
        op.finTeorico,
      ]);
      if (!inicio || !fin) return null;

      const machineName = this.pickFirstText([
        this.firstMachineName(op.maquinasAsignadas),
        op.nombreMaquina,
        op.maquina,
        op.maquinaNombre,
        op.claveMaquina,
        this.firstMachineName(op.maquinasInstanciaPosibles),
        this.firstMachineName(op.maquinasPosibles),
      ]);

      return {
        idTrabajoOrden: this.pickFirstNumber([odt && odt.idTrabajoOrden]) || 0,
        idTrabajoOrdenDetalle: this.pickFirstNumber([op && op.idTrabajoOrdenDetalle]) || 0,
        idOperacion: this.pickFirstNumber([op && op.idOperacion]) || 0,
        idOperacionReal: isReal ? this.pickFirstNumber([op && op.idOperacionReal, op && op.idOperacion]) : null,
        idReceta: this.pickFirstNumber([op && op.idReceta]),
        idPaso: this.pickFirstNumber([op && op.idPaso]),
        numPaso: this.pickFirstNumber([op && op.numPaso]),
        claveTrabajoOrden: this.pickFirstText([
          odt && odt.claveODT,
          odt && odt.claveTrabajoOrden,
        ]),
        articulo: this.pickFirstText([op && op.articulo]),
        codigoArticulo: this.pickFirstText([op && op.codigoArticulo, op && op.articulo]),
        cliente: this.pickFirstText([odt && odt.cliente]),
        prioridad: this.pickFirstNumber([odt && odt.prioridad]) || 0,
        receta: this.pickFirstText([op && op.area]),
        area: this.pickFirstText([op && op.area]),
        inicio,
        fin,
        inicioReal: isReal ? inicio : "",
        finReal: isReal ? fin : "",
        duracionSeg: this.pickFirstNumber([
          isReal ? op && op.duracionSegundos : null,
          op && op.duracionTeoricaSegundos,
        ]) || this.diffSeconds(inicio, fin),
        estadoId: isReal ? 4 : 4,
        estado: isReal ? "Produccion" : "Programado",
        esTeorica: !isReal,
        nombreMaquina: machineName,
        maquina: machineName,
        maquinasPosibles: this.pickFirstArray([op && op.maquinasPosibles]),
        maquinasInstanciaPosibles: this.pickFirstArray([op && op.maquinasInstanciaPosibles]),
        maquinasAsignadas: this.pickFirstArray([op && op.maquinasAsignadas]),
        ingredientes: this.pickFirstArray([op && op.ingredientes]),
        piezasPlaneadas: this.pickFirstNumber([op && op.piezasPlaneadas]),
        piezasReportadas: this.pickFirstArray([op && op.piezasRegistradas]),
        piezasReportadasTotal: this.sumReportedPieces(op && op.piezasRegistradas, op && op.piezasReportadas),
        tiempoCicloSegundos: this.pickFirstNumber([op && op.tiempoCicloSegundos]),
        tiempoExtraSegundos: this.pickFirstNumber([op && op.tiempoExtraSegundos]),
        tieneAsignacionReal: !!(op && op.tieneAsignacionReal),
        inicioTeoricoRef: this.pickFirstText([teoricoMatch && teoricoMatch.inicioTeorico]),
        finTeoricoRef: this.pickFirstText([teoricoMatch && teoricoMatch.finTeorico]),
        subpasos: this.pickFirstArray([op && op.subpasos]),
        subpasosPendientes: this.pickFirstArray([op && op.subpasosPendientes]),
        subpasosEnProceso: this.pickFirstArray([op && op.subpasosEnProceso]),
        subpasosCumplidos: this.pickFirstArray([op && op.subpasosCumplidos]),
        operarios: this.pickFirstArray([op && op.operarios]),
        paros: this.pickFirstArray([op && op.paros]),
      };
    },
    findMatchingTheoreticalOperation(odt, realOp) {
      const teorico = this.pickFirstArray([odt && odt.teorico]);
      if (!teorico.length || !realOp) return null;
      const realIdOperacion = this.pickFirstNumber([realOp && realOp.idOperacion]);
      const realIdDetalle = this.pickFirstNumber([realOp && realOp.idTrabajoOrdenDetalle]);
      const realIdPaso = this.pickFirstNumber([realOp && realOp.idPaso]);
      const realNumPaso = this.pickFirstNumber([realOp && realOp.numPaso]);

      return teorico.find((item) => {
        const sameOperacion = this.pickFirstNumber([item && item.idOperacion]) === realIdOperacion;
        const sameDetalle = this.pickFirstNumber([item && item.idTrabajoOrdenDetalle]) === realIdDetalle;
        const samePaso = this.pickFirstNumber([item && item.idPaso]) === realIdPaso;
        const sameNumPaso = this.pickFirstNumber([item && item.numPaso]) === realNumPaso;
        return (sameOperacion && sameDetalle) || (sameDetalle && samePaso) || (sameDetalle && sameNumPaso);
      }) || null;
    },
    sumReportedPieces(registros, fallback = null) {
      const source = Array.isArray(registros) ? registros : [];
      if (source.length) {
        return source.reduce((acc, item) => acc + (Number(item && item.cantidad) || 0), 0);
      }
      const numeric = Number(fallback);
      return Number.isFinite(numeric) ? numeric : null;
    },
    firstMachineName(values) {
      const source = Array.isArray(values) ? values : [];
      for (let i = 0; i < source.length; i += 1) {
        if (typeof source[i] === "string" && source[i].trim()) return source[i].trim();
      }
      return "";
    },
    diffSeconds(startValue, endValue) {
      const start = new Date(startValue);
      const end = new Date(endValue);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
      return Math.max(0, Math.round((end.getTime() - start.getTime()) / 1000));
    },
    buildMachinesFromMitcen(blocks, odtDetailMap) {
      const source = Array.isArray(blocks) ? blocks : [];
      return source
        .map((machineBlock) => {
          const machine = this.resolveMitcenMachine(machineBlock);
          if (!machine) return null;
          const rows = this.buildMitcenRows(machineBlock, machine, odtDetailMap);
          return {
            key: machine.key,
            name: machine.name,
            rows,
          };
        })
        .filter((machine) => machine && machine.name)
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    resolveMitcenMachine(machineBlock) {
      const machineId = this.pickFirstNumber([
        machineBlock && machineBlock.idMaquina,
        machineBlock && machineBlock.IdMaquina,
      ]);
      const machineName = this.pickFirstText([
        machineBlock && machineBlock.nombreMaquina,
        machineBlock && machineBlock.NombreMaquina,
      ]);
      if (machineId === null && !machineName) return null;
      return {
        key: machineId !== null
          ? `M-${machineId}`
          : `M-${(machineName || "SIN_MAQUINA").toUpperCase().replace(/\s+/g, "_")}`,
        name: machineName || (machineId !== null ? `Maq ${machineId}` : ""),
      };
    },
    buildMitcenRows(machineBlock, machine, odtDetailMap) {
      const grouped = {};
      const fragments = this.extractMitcenFragments(machineBlock);
      fragments.forEach((fragment) => {
        const normalized = this.normalizeMitcenFragment(fragment, machineBlock);
        if (!normalized) return;
        const rowKey = normalized.rowKey;
        if (!grouped[rowKey]) {
          grouped[rowKey] = {
            key: rowKey,
            mold: normalized.rowLabel,
            base: normalized.base,
            segments: [],
            detalleOps: [],
          };
        }
        grouped[rowKey].segments.push(normalized.segment);
        grouped[rowKey].detalleOps.push(normalized.detalle);
      });

      return Object.values(grouped)
        .map((group) => {
          group.segments.sort((a, b) => (a.day === b.day ? a.startMin - b.startMin : a.day.localeCompare(b.day)));
          group.detalleOps.sort((a, b) => String(a.inicio).localeCompare(String(b.inicio)));
          const merged = this.mergeConsecutiveFragments(group.detalleOps, group.segments);
          group.detalleOps = merged.detalleOps.map((item, idx) => ({
            ...item,
            fragmentoNumero: idx + 1,
          }));
          group.segments = merged.segments.map((item, idx) => ({
            ...item,
            fragmentoNumero: idx + 1,
          }));
          const rowDetail = odtDetailMap && odtDetailMap[group.key] ? odtDetailMap[group.key] : null;
          const odt = this.buildOdt(group.base, group.detalleOps, rowDetail);
          group.segments.forEach((seg) => { seg.odt = odt; });
          return {
            key: group.key,
            mold: group.mold,
            odt,
            segments: group.segments,
          };
        })
        .sort((a, b) => a.mold.localeCompare(b.mold));
    },
    extractMitcenFragments(machineBlock) {
      const reales = this.pickFirstArray([
        machineBlock && machineBlock.fragmentosReales,
        machineBlock && machineBlock.FragmentosReales,
      ]);
      const teoricos = this.pickFirstArray([
        machineBlock && machineBlock.fragmentosTeoricos,
        machineBlock && machineBlock.FragmentosTeoricos,
      ]);
      return reales.length ? reales : teoricos;
    },
    normalizeMitcenFragment(fragment, machineBlock) {
      if (!fragment || typeof fragment !== "object") return null;
      const inicio = this.pickFirstText([
        fragment.Inicio,
        fragment.inicio,
      ]);
      const fin = this.pickFirstText([
        fragment.Fin,
        fragment.fin,
      ]);
      if (!inicio || !fin) return null;
      const op = {
        idTrabajoOrden: this.pickFirstNumber([fragment.IdTrabajoOrden, fragment.idTrabajoOrden]) || 0,
        idTrabajoOrdenDetalle: this.pickFirstNumber([fragment.IdTrabajoOrdenDetalle, fragment.idTrabajoOrdenDetalle]) || 0,
        idOperacion: this.pickFirstNumber([fragment.IdOperacion, fragment.idOperacion]) || 0,
        idOperacionReal: this.pickFirstNumber([fragment.IdOperacionReal, fragment.idOperacionReal]),
        idMaquina: this.pickFirstNumber([
          fragment.IdMaquina,
          fragment.idMaquina,
          machineBlock && machineBlock.IdMaquina,
          machineBlock && machineBlock.idMaquina,
        ]),
        nombreMaquina: this.pickFirstText([
          fragment.NombreMaquina,
          fragment.nombreMaquina,
          machineBlock && machineBlock.NombreMaquina,
          machineBlock && machineBlock.nombreMaquina,
        ]),
        claveTrabajoOrden: this.pickFirstText([fragment.NombreODT, fragment.nombreODT]),
        articulo: this.pickFirstText([fragment.Articulo, fragment.articulo]),
        codigoArticulo: this.pickFirstText([fragment.CodigoArticulo, fragment.codigoArticulo, fragment.Articulo, fragment.articulo]),
        receta: this.pickFirstText([fragment.Area, fragment.area]),
        area: this.pickFirstText([fragment.Area, fragment.area]),
        molde: this.pickFirstText([fragment.Molde, fragment.molde]),
        inicio,
        fin,
        // horaInicio: this.extractTime(inicio), //los siento un poco repetitivo, descomentar en caso de ser necesario
        // horaFin: this.extractTime(fin),
        duracionSeg: this.resolveFragmentDuration(fragment, inicio, fin),
        estadoId: this.resolveMitcenEstadoId(fragment),
        estado: this.pickFirstText([fragment.Estado, fragment.estado]),
        esTeorica: this.resolveMitcenEsTeorica(fragment, machineBlock),
        operaciones: this.pickFirstArray([fragment.Operaciones, fragment.operaciones]),
      };
      const rowKey = this.buildMitcenRowKey(op);
      return {
        rowKey,
        rowLabel: this.buildMitcenRowLabel(op),
        base: op,
        detalle: this.toDetalleOperacion(op, true, null),
        segment: this.toSegment(op, this.toDateKey(inicio), true),
      };
    },
    resolveFragmentDuration(fragment, inicio, fin) {
      const duration = this.pickFirstNumber([
        fragment.DuracionSeg,
        fragment.duracionSeg,
      ]);
      if (duration !== null) return duration;
      const start = new Date(inicio);
      const end = new Date(fin);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
      return Math.max(0, Math.round((end.getTime() - start.getTime()) / 1000));
    },
    resolveMitcenEstadoId(fragment) {
      const explicit = this.pickFirstNumber([
        fragment.EstadoId,
        fragment.estadoId,
      ]);
      if (explicit !== null) return explicit;
      const estado = this.pickFirstText([fragment.Estado, fragment.estado]).toLowerCase();
      if (estado.includes("paro program")) return 2;
      if (estado.includes("paro")) return 3;
      if (estado.includes("retras")) return 5;
      if (estado.includes("muert")) return 6;
      if (estado.includes("retrab")) return 7;
      if (estado.includes("cambio")) return 1;
      return 4;
    },
    resolveMitcenEsTeorica(fragment, machineBlock) {
      const explicit = fragment && fragment.EsTeorica !== undefined
        ? fragment.EsTeorica
        : fragment && fragment.esTeorica !== undefined
          ? fragment.esTeorica
          : null;
      if (typeof explicit === "boolean") return explicit;
      const reales = this.pickFirstArray([
        machineBlock && machineBlock.fragmentosReales,
        machineBlock && machineBlock.FragmentosReales,
      ]);
      return !reales.length;
    },
    buildRowsByMachine(blocks, odtDetailMap) {
      const source = Array.isArray(blocks) ? blocks : [];
      const machines = {};

      source.forEach((block) => {
        const dayKey = this.toDateKey(block && block.fecha);
        const operaciones = Array.isArray(block && block.operaciones) ? block.operaciones : [];
        operaciones.forEach((op) => {
          const rowKey = this.buildRowKey(op);
          const rowDetail = odtDetailMap && odtDetailMap[rowKey] ? odtDetailMap[rowKey] : null;
          const machine = this.resolveMachineInfo(op, block, rowDetail);
          if (!machine || !machine.name) return;
          if (!machines[machine.key]) {
            machines[machine.key] = {
              name: machine.name,
              grouped: {},
            };
          }
          const grouped = machines[machine.key].grouped;
          const key = rowKey;
          if (!grouped[key]) {
            grouped[key] = {
              key,
              mold: this.buildRowLabel(op),
              base: { ...op },
              segments: [],
              detalleOps: [],
            };
          }
          const detalle = this.toDetalleOperacion(op, true, rowDetail);
          const segment = this.toSegment(op, dayKey, true);
          if (segment) grouped[key].segments.push(segment);
          grouped[key].detalleOps.push(detalle);
        });
      });

      const response = {};
      Object.keys(machines).forEach((machineKey) => {
        const machine = machines[machineKey];
        const rows = Object.values(machine.grouped)
          .map((group) => {
            group.segments.sort((a, b) => (a.day === b.day ? a.startMin - b.startMin : a.day.localeCompare(b.day)));
            group.detalleOps.sort((a, b) => String(a.inicio).localeCompare(String(b.inicio)));
            const merged = this.mergeConsecutiveFragments(group.detalleOps, group.segments);
            group.detalleOps = merged.detalleOps.map((item, idx) => ({
              ...item,
              fragmentoNumero: idx + 1,
            }));
            group.segments = merged.segments.map((item, idx) => ({
              ...item,
              fragmentoNumero: idx + 1,
            }));
            const rowDetail = odtDetailMap && odtDetailMap[group.key] ? odtDetailMap[group.key] : null;
            const odt = this.buildOdt(group.base, group.detalleOps, rowDetail);
            group.segments.forEach((seg) => { seg.odt = odt; });
            return {
              key: group.key,
              mold: group.mold,
              odt,
              segments: group.segments,
            };
          })
          .sort((a, b) => a.mold.localeCompare(b.mold));
        response[machineKey] = {
          name: machine.name,
          rows,
        };
      });
      return response;
    },
    resolveMachineInfo(op, block, rowDetail) {
      const machineId = this.pickFirstNumber([
        op && op.idMaquina,
        op && op.maquinaId,
        op && op.idRecurso,
        block && block.idMaquina,
        block && block.maquinaId,
        rowDetail && rowDetail.machineId,
      ]);
      const machineName = this.pickFirstText([
        op && op.maquina,
        op && op.nombreMaquina,
        op && op.maquinaNombre,
        op && op.claveMaquina,
        op && op.nombreRecurso,
        block && block.maquina,
        block && block.nombreMaquina,
        block && block.maquinaNombre,
        rowDetail && rowDetail.machineName,
      ]);
      if (machineId === null && !machineName) return null;
      const key = machineId !== null
        ? `M-${machineId}`
        : `M-${(machineName || "SIN_MAQUINA").toUpperCase().replace(/\s+/g, "_")}`;
      const name = machineName || (machineId !== null ? `Maq ${machineId}` : "");
      return {
        key,
        name,
      };
    },
    pickFirstNumber(values) {
      for (let i = 0; i < values.length; i += 1) {
        if (values[i] === null || values[i] === undefined || values[i] === "") continue;
        const value = Number(values[i]);
        if (Number.isFinite(value)) return value;
      }
      return null;
    },
    pickFirstText(values) {
      for (let i = 0; i < values.length; i += 1) {
        const value = values[i];
        if (typeof value === "string" && value.trim()) return value.trim();
      }
      return "";
    },
    pickFirstArray(values) {
      for (let i = 0; i < values.length; i += 1) {
        if (Array.isArray(values[i])) return values[i];
      }
      return [];
    },
    mergeDistinctArrayValues(...lists) {
      const values = [];
      lists.forEach((list) => {
        const source = Array.isArray(list) ? list : [];
        source.forEach((item) => {
          if (typeof item !== "string") return;
          const clean = item.trim();
          if (clean && !values.includes(clean)) values.push(clean);
        });
      });
      return values;
    },
    mergeConsecutiveFragments(detalleOps, segments) {
      const details = Array.isArray(detalleOps) ? detalleOps : [];
      const segs = Array.isArray(segments) ? segments : [];
      const total = Math.min(details.length, segs.length);
      const mergedDetails = [];
      const mergedSegments = [];

      for (let i = 0; i < total; i += 1) {
        const currentDetail = { ...details[i] };
        const currentSegment = { ...segs[i] };
        const lastIndex = mergedSegments.length - 1;
        if (lastIndex >= 0 && this.areConsecutiveSegments(mergedSegments[lastIndex], currentSegment)) {
          mergedSegments[lastIndex] = this.mergeSegments(mergedSegments[lastIndex], currentSegment);
          mergedDetails[lastIndex] = this.mergeDetalleOps(mergedDetails[lastIndex], currentDetail);
          continue;
        }
        mergedDetails.push(currentDetail);
        mergedSegments.push(currentSegment);
      }
      for (let i = total; i < details.length; i += 1) {
        mergedDetails.push({ ...details[i] });
      }
      for (let i = total; i < segs.length; i += 1) {
        mergedSegments.push({ ...segs[i] });
      }

      return {
        detalleOps: mergedDetails,
        segments: mergedSegments,
      };
    },
    areConsecutiveSegments(prev, next) {
      if (!prev || !next) return false;
      if (prev.day !== next.day) return false;
      if (prev.type !== next.type) return false;
      const prevEnd = Number(prev.endMin);
      const nextStart = Number(next.startMin);
      if (!Number.isFinite(prevEnd) || !Number.isFinite(nextStart)) return false;
      return prevEnd === nextStart;
    },
    mergeSegments(base, next) {
      return {
        ...base,
        end: next.end,
        endMin: next.endMin,
        articulo: this.mergeDistinctLabels(base.articulo, next.articulo),
        codigoArticulo: this.mergeDistinctLabels(base.codigoArticulo, next.codigoArticulo),
        area: this.mergeDistinctLabels(base.area, next.area),
        finTexto: next.finTexto || base.finTexto,
      };
    },
    mergeDistinctLabels(...items) {
      const values = [];
      items.forEach((item) => {
        if (typeof item !== "string" || !item.trim()) return;
        item.split(",").forEach((part) => {
          const clean = part.trim();
          if (clean && !values.includes(clean)) values.push(clean);
        });
      });
      return values.join(", ");
    },
    mergeDetalleOps(base, next) {
      return {
        ...base,
        fin: next.fin || base.fin,
        finReal: next.finReal || base.finReal,
        horaFin: next.horaFin || base.horaFin,
        duracionSeg: (Number(base.duracionSeg) || 0) + (Number(next.duracionSeg) || 0),
        esTeorica: Boolean(base.esTeorica) && Boolean(next.esTeorica),
        cantidad: this.sumNullableNumbers(base.cantidad, next.cantidad),
        cantidadHechaTotal: this.pickLastNumeric(base.cantidadHechaTotal, next.cantidadHechaTotal),
        cantidadNoOk: this.sumNullableNumbers(base.cantidadNoOk, next.cantidadNoOk),
        cantidadScrap: this.sumNullableNumbers(base.cantidadScrap, next.cantidadScrap),
        cantidadRetrabajo: this.sumNullableNumbers(base.cantidadRetrabajo, next.cantidadRetrabajo),
        paros: [
          ...(Array.isArray(base.paros) ? base.paros : []),
          ...(Array.isArray(next.paros) ? next.paros : []),
        ],
        fragmentoKey: `${base.fragmentoKey || ""}|${next.fragmentoKey || ""}`,
      };
    },
    sumNullableNumbers(left, right) {
      const hasLeft = left !== null && left !== undefined && Number.isFinite(Number(left));
      const hasRight = right !== null && right !== undefined && Number.isFinite(Number(right));
      if (!hasLeft && !hasRight) return null;
      return (hasLeft ? Number(left) : 0) + (hasRight ? Number(right) : 0);
    },
    pickLastNumeric(left, right) {
      if (right !== null && right !== undefined && Number.isFinite(Number(right))) return Number(right);
      if (left !== null && left !== undefined && Number.isFinite(Number(left))) return Number(left);
      return null;
    },
    buildOdtDetailMap(odts) {
      const map = {};
      const source = Array.isArray(odts) ? odts : [];
      source.forEach((odt) => {
        const idTrabajoOrden = odt && odt.idTrabajoOrden != null ? odt.idTrabajoOrden : 0;
        const operations = [
          ...(odt && Array.isArray(odt.operaciones) ? odt.operaciones : []),
          ...(odt && Array.isArray(odt.teorico) ? odt.teorico : []),
          ...(odt && Array.isArray(odt.real) ? odt.real : []),
        ];
        operations.forEach((op) => {
          const idOperacion = op && op.idOperacion != null ? op.idOperacion : 0;
          const idTrabajoOrdenDetalle = op && op.idTrabajoOrdenDetalle != null ? op.idTrabajoOrdenDetalle : 0;
          const key = `${idTrabajoOrden}-${idTrabajoOrdenDetalle}-${idOperacion}`;
          const pasos = op && Array.isArray(op.pasos) ? op.pasos : [];
          const ingredientes = [];

          const directIngredients = op && Array.isArray(op.ingredientes) ? op.ingredientes : [];
          directIngredients.forEach((ingredient) => {
            ingredientes.push({
              idPaso: op && op.idPaso != null ? op.idPaso : null,
              paso: op && op.numPaso != null ? op.numPaso : null,
              area: op && op.area ? op.area : "",
              tiempoPasoSeg: op && op.duracionTeoricaSegundos != null ? op.duracionTeoricaSegundos : null,
              tipo: ingredient && ingredient.tipo ? ingredient.tipo : "",
              ingrediente: ingredient && (ingredient.ingrediente || ingredient.nombre || ingredient.descripcion)
                ? (ingredient.ingrediente || ingredient.nombre || ingredient.descripcion)
                : "",
              codigoArticulo: ingredient && ingredient.codigoArticulo ? ingredient.codigoArticulo : "",
              cantidad: ingredient && ingredient.cantidad != null ? ingredient.cantidad : null,
              unidad: ingredient && ingredient.unidad ? ingredient.unidad : "",
              tiempoSeg: ingredient && ingredient.tiempoSeg != null ? ingredient.tiempoSeg : null,
            });
          });

          pasos.forEach((paso) => {
            const sourceIngredients = paso && Array.isArray(paso.ingredientes) ? paso.ingredientes : [];
            sourceIngredients.forEach((ingredient) => {
              ingredientes.push({
                idPaso: paso && paso.idPaso != null ? paso.idPaso : null,
                paso: paso && paso.numPaso != null ? paso.numPaso : null,
                area: paso && paso.area ? paso.area : "",
                tiempoPasoSeg: paso && paso.tiempoPasoSeg != null ? paso.tiempoPasoSeg : null,
                tipo: ingredient && ingredient.tipo ? ingredient.tipo : "",
                ingrediente: ingredient && ingredient.ingrediente ? ingredient.ingrediente : "",
                codigoArticulo: ingredient && ingredient.codigoArticulo ? ingredient.codigoArticulo : "",
                cantidad: ingredient && ingredient.cantidad != null ? ingredient.cantidad : null,
                unidad: ingredient && ingredient.unidad ? ingredient.unidad : "",
                tiempoSeg: ingredient && ingredient.tiempoSeg != null ? ingredient.tiempoSeg : null,
              });
            });
          });
          const machineIngredient = ingredientes.find((item) =>
            typeof item.tipo === "string" && item.tipo.toLowerCase() === "maquina"
          );
          map[key] = {
            machineName: this.pickFirstText([
              machineIngredient && machineIngredient.ingrediente,
              this.firstMachineName(op && op.maquinasInstanciaPosibles),
              this.firstMachineName(op && op.maquinasPosibles),
            ]),
            machineId: null,
            area: this.pickFirstText([
              op && op.area,
              pasos.length && pasos[0] && pasos[0].area ? pasos[0].area : "",
            ]),
            ingredientes,
          };
        });
      });
      return map;
    },
    buildRowKey(op) {
      const ot = op && op.idTrabajoOrden != null ? op.idTrabajoOrden : 0;
      const otd = op && op.idTrabajoOrdenDetalle != null ? op.idTrabajoOrdenDetalle : 0;
      const idOp = op && op.idOperacion != null ? op.idOperacion : 0;
      return `${ot}-${otd}-${idOp}`;
    },
    buildRowLabel(op) {
      const clave = this.pickFirstText([
        op && op.claveTrabajoOrden,
        op && op.claveODT,
      ]) || "N/D";
      const art = op && (op.codigoArticulo || op.articulo) ? (op.codigoArticulo || op.articulo) : "N/D";
      return `ODT: ${clave} | Articulo: ${art}`;
    },
    buildMitcenRowLabel(op) {
      const base = this.buildRowLabel(op);
      const molde = this.pickFirstText([op && op.molde]);
      return molde ? `${base} | Molde: ${molde}` : base;
    },
    buildMitcenRowKey(op) {
      const baseKey = this.buildRowKey(op);
      if (baseKey !== "0-0-0") return baseKey;
      const fallback = [
        this.pickFirstText([op && op.claveTrabajoOrden, "SIN_ODT"]),
        this.pickFirstText([op && op.codigoArticulo, op && op.articulo, "SIN_ART"]),
        this.pickFirstText([op && op.molde, "SIN_MOLDE"]),
        this.pickFirstText([op && op.inicio, "SIN_INICIO"]),
      ];
      return fallback.join("|");
    },
    buildFragmentKey(op, estadoFallback = 0, inicioFallback = "", finFallback = "") {
      const idOperacion = op && op.idOperacion != null ? op.idOperacion : 0;
      const estadoId = Number(op && op.estadoId != null ? op.estadoId : estadoFallback) || 0;
      const idOperacionReal = op && op.idOperacionReal != null ? op.idOperacionReal : 0;
      const idParo = op && op.idParo != null ? op.idParo : 0;
      const inicio = op && op.inicio ? op.inicio : inicioFallback;
      const fin = op && op.fin ? op.fin : finFallback;
      const esTeorica = op && op.esTeorica ? 1 : 0;
      return `${idOperacion}|${estadoId}|${idOperacionReal}|${idParo}|${inicio}|${fin}|${esTeorica}`;
    },
    resolveEstadoTexto(op, estadoId) {
      return this.pickFirstText([
        op && op.estado,
        this.estadoToLabel(estadoId),
      ]) || "Programado";
    },
    toDetalleOperacion(op, isReal, rowDetail) {
      const estado = isReal ? Number(op && op.estadoId != null ? op.estadoId : 0) : 4;
      const inicio = op && op.inicio ? op.inicio : "";
      const fin = op && op.fin ? op.fin : "";
      return {
        nombre: op && (op.codigoArticulo || op.idOperacion != null)
          ? (op.codigoArticulo || `Operacion ${op.idOperacion}`)
          : "Operacion",
        idOperacion: op && op.idOperacion != null ? op.idOperacion : null,
        idOperacionReal: op && op.idOperacionReal != null ? op.idOperacionReal : null,
        idTrabajoOrdenDetalle: op && op.idTrabajoOrdenDetalle != null ? op.idTrabajoOrdenDetalle : null,
        idPaso: op && op.idPaso != null ? op.idPaso : null,
        numPaso: op && op.numPaso != null ? op.numPaso : null,
        area: this.pickFirstText([
          rowDetail && rowDetail.area,
          op && op.area,
          op && op.receta,
          op && op.articulo,
        ]) || "N/D",
        articulo: this.pickFirstText([op && op.articulo]),
        codigoArticulo: this.pickFirstText([op && op.codigoArticulo, op && op.articulo]),
        inicio,
        fin,
        inicioReal: op && op.inicioReal ? op.inicioReal : "",
        finReal: op && op.finReal ? op.finReal : "",
        horaInicio: op && op.horaInicio ? this.extractTime(op.horaInicio) : this.extractTime(inicio),
        horaFin: op && op.horaFin ? this.extractTime(op.horaFin) : this.extractTime(fin),
        estado,
        estadoId: estado,
        estadoTexto: isReal ? this.resolveEstadoTexto(op, estado) : "Programado",
        duracionSeg: Number(op && op.duracionSeg != null ? op.duracionSeg : 0) || 0,
        esTeorica: !!(op && op.esTeorica),
        idParo: op && op.idParo != null ? op.idParo : null,
        idParoTipo: op && op.idParoTipo != null ? op.idParoTipo : null,
        paroTipo: op && op.paroTipo ? op.paroTipo : "",
        idSubParoTipo: op && op.idSubParoTipo != null ? op.idSubParoTipo : null,
        subParoTipo: op && op.subParoTipo ? op.subParoTipo : "",
        idTrabajoOrden: op && op.idTrabajoOrden != null ? op.idTrabajoOrden : null,
        claveTrabajoOrden: op && op.claveTrabajoOrden ? op.claveTrabajoOrden : "",
        idReceta: op && op.idReceta != null ? op.idReceta : null,
        receta: op && op.receta ? op.receta : "",
        cantidad: op && op.cantidad != null ? op.cantidad : null,
        piezasPlaneadas: op && op.piezasPlaneadas != null ? op.piezasPlaneadas : null,
        piezasReportadasTotal: op && op.piezasReportadasTotal != null ? op.piezasReportadasTotal : null,
        diferenciaPiezas: this.resolvePieceDifference(op),
        maquinaAsignada: this.mergeDistinctLabels(
          this.pickFirstText([op && op.maquinaAsignada, op && op.maquina, op && op.nombreMaquina]),
          ...this.pickFirstArray([op && op.maquinasAsignadas])
        ),
        maquinasDisponibles: this.pickFirstArray([op && op.maquinasPosibles]),
        tiempoCicloSegundos: op && op.tiempoCicloSegundos != null ? op.tiempoCicloSegundos : null,
        tiempoExtraSegundos: op && op.tiempoExtraSegundos != null ? op.tiempoExtraSegundos : null,
        tieneAsignacionReal: !!(op && op.tieneAsignacionReal),
        subpasos: this.pickFirstArray([op && op.subpasos]),
        subpasosPendientes: this.pickFirstArray([op && op.subpasosPendientes]),
        subpasosEnProceso: this.pickFirstArray([op && op.subpasosEnProceso]),
        subpasosCumplidos: this.pickFirstArray([op && op.subpasosCumplidos]),
        operarios: this.pickFirstArray([op && op.operarios]),
        paros: this.pickFirstArray([op && op.paros]),
        cantidadHechaTotal: op && op.cantidadHechaTotal != null ? op.cantidadHechaTotal : null,
        cantidadNoOk: op && op.cantidadNoOk != null ? op.cantidadNoOk : null,
        cantidadScrap: op && op.cantidadScrap != null ? op.cantidadScrap : null,
        cantidadRetrabajo: op && op.cantidadRetrabajo != null ? op.cantidadRetrabajo : null,
        fragmentoKey: this.buildFragmentKey(op, estado, inicio, fin),
      };
    },
    resolvePieceDifference(op) {
      const planned = Number(op && op.piezasPlaneadas);
      const reported = Number(op && op.piezasReportadasTotal);
      if (!Number.isFinite(planned) && !Number.isFinite(reported)) return null;
      return (Number.isFinite(planned) ? planned : 0) - (Number.isFinite(reported) ? reported : 0);
    },
    buildOdt(op, operaciones, rowDetail) {
      const ops = Array.isArray(operaciones) ? operaciones : [];
      const total = ops.reduce((acc, item) => acc + (Number(item.duracionSeg) || 0), 0);
      const prod = ops.reduce((acc, item) => (Number(item.estado) === 4 ? acc + (Number(item.duracionSeg) || 0) : acc), 0);
      const totalTeorico = ops.reduce((acc, item) => (item.esTeorica ? acc + (Number(item.duracionSeg) || 0) : acc), 0);
      const totalDesfase = ops.reduce((acc, item) => (Number(item.estado) === 5 ? acc + (Number(item.duracionSeg) || 0) : acc), 0);
      const first = ops.length ? ops[0] : null;
      const last = ops.length ? ops[ops.length - 1] : null;
      const maquinasDisponibles = this.mergeDistinctArrayValues(
        op && op.maquinasDisponibles,
        ...ops.map((item) => item && (item.maquinasDisponibles || item.maquinasPosibles))
      );
      return {
        clave: this.pickFirstText([
          op && op.claveTrabajoOrden,
          op && op.claveODT,
        ]) || "N/D",
        descripcion: op && op.articulo ? op.articulo : "Sin descripcion",
        codigoArticulo: op && op.codigoArticulo ? op.codigoArticulo : "",
        articulo: op && op.articulo ? op.articulo : "",
        idOperacion: op && op.idOperacion != null ? op.idOperacion : null,
        cliente: this.pickFirstText([op && op.cliente]),
        maquinaAsignada: this.pickFirstText([
          op && op.maquinaAsignada,
          op && op.maquina,
          op && op.nombreMaquina,
        ]),
        maquinasDisponibles,
        lineaComercial: "",
        fechaInicioProduccion: this.pickFirstText([
          op && op.fechaInicioProduccion,
          first ? first.inicio : "",
          op && op.inicio ? op.inicio : "",
        ]),
        fechaFinTeorica: this.pickFirstText([
          op && op.fechaFinTeorica,
          last ? last.fin : "",
        ]),
        fechaEntrega: this.pickFirstText([
          op && op.fechaEntrega,
          last ? last.fin : "",
          op && op.fin ? op.fin : "",
        ]),
        porcentajeCompletado: total > 0 ? (prod / total) * 100 : 0,
        costoEstimadoTotal: 0,
        costoReal: 0,
        estadoProduccion: last ? Number(last.estado) || 0 : 0,
        desfaseSeg: totalDesfase,
        diferenciaSeg: total - totalTeorico,
        planView: ops.some((item) => item && item.esTeorica === false) ? "real" : "teorico",
        ingredientes: rowDetail && Array.isArray(rowDetail.ingredientes) ? rowDetail.ingredientes : [],
        operaciones: ops,
        materiales: [],
        notas: "",
      };
    },
    toSegment(op, dayKey, isReal) {
      const targetDay = this.toDateKey(op && op.inicio) || this.toDateKey(op && op.fin) || dayKey;
      if (!targetDay) return null;
      const startValue = op && op.horaInicio ? op.horaInicio : this.extractTime(op && op.inicio);
      const endValue = op && op.horaFin ? op.horaFin : this.extractTime(op && op.fin);
      const startMin = this.timeToMinutes(startValue);
      let endMin = this.timeToMinutes(endValue);
      if (!Number.isFinite(startMin) || !Number.isFinite(endMin)) return null;
      if (endMin < startMin) endMin += 24 * 60;
      const estadoId = isReal ? Number(op && op.estadoId != null ? op.estadoId : 0) : 4;
      const start = this.extractTime(startValue) || this.minutesToTime(startMin);
        const end = this.extractTime(endValue) || this.minutesToTime(endMin);
        return {
          day: targetDay,
          start,
          end,
        startMin,
        endMin,
        fullStart: op && op.inicio ? op.inicio : "",
        fullEnd: op && op.fin ? op.fin : "",
          planView: isReal ? "real" : "teorico",
          type: isReal ? this.estadoToType(estadoId) : "programado",
          estadoId,
          estado: isReal ? this.resolveEstadoTexto(op, estadoId) : "Programado",
          isSyntheticDelaySummary: !!(op && op.isSyntheticDelaySummary),
          idOperacion: op && op.idOperacion != null ? op.idOperacion : null,
          articulo: this.pickFirstText([op && op.articulo]),
        codigoArticulo: this.pickFirstText([op && op.codigoArticulo, op && op.articulo]),
        area: this.pickFirstText([op && op.area, op && op.receta]),
        maquinaAsignada: this.mergeDistinctLabels(
          this.pickFirstText([op && op.maquinaAsignada, op && op.maquina, op && op.nombreMaquina]),
          ...this.pickFirstArray([op && op.maquinasAsignadas])
          ),
            paros: this.pickFirstArray([op && op.paros]),
            piezasPlaneadasTexto: this.formatTooltipNumber(op && op.piezasPlaneadas),
            piezasReportadasTexto: this.formatTooltipNumber(op && op.piezasReportadasTotal),
            tiempoExtraSegundos: this.pickFirstNumber([op && op.tiempoExtraSegundos]),
            inicioTeoricoRef: this.pickFirstText([op && op.inicioTeoricoRef]),
            finTeoricoRef: this.pickFirstText([op && op.finTeoricoRef]),
            finTexto: op && op.fin ? this.formatTooltipDateTime(op.fin) : "",
            fragmentoKey: this.buildFragmentKey(op, estadoId, start, end),
          };
        },
    formatTooltipNumber(value) {
      if (value === null || value === undefined || value === "") return "";
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return "";
      return `${numeric}`;
    },
    formatTooltipDateTime(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      const dd = String(date.getDate()).padStart(2, "0");
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const yyyy = date.getFullYear();
      const hh = String(date.getHours()).padStart(2, "0");
      const min = String(date.getMinutes()).padStart(2, "0");
      return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
    },
    formatDurationHoursMinutes(value) {
      const seconds = Number(value);
      if (!Number.isFinite(seconds)) return "00:00";
      const absolute = Math.abs(Math.round(seconds));
      const hh = String(Math.floor(absolute / 3600)).padStart(2, "0");
      const mm = String(Math.floor((absolute % 3600) / 60)).padStart(2, "0");
      return `${hh}:${mm}`;
    },
    estadoToLabel(estadoId) {
      const found = this.estadoBaseMap[String(Number(estadoId))];
      if (found) return found.label;
      switch (Number(estadoId)) {
        case 7:
          return "Retrabajo";
        default:
          return "Programado";
      }
    },
    estadoToType(estadoId) {
      const found = this.estadoBaseMap[String(Number(estadoId))];
      if (found) return found.type;
      switch (Number(estadoId)) {
        case 7:
          return "retrabajo";
        default:
          return "programado";
      }
    },
    segmentTitle(seg) {
      if (!seg) return "";
      const lines = [];
      if (seg.isOutsideSchedule) {
        lines.push("Fuera de horario");
        return lines.join("\n");
      }
        if (seg.isParo) {
          lines.push(`Concepto: ${seg.conceptoParo || "N/D"}`);
          lines.push(`Duracion: ${this.formatDurationHoursMinutes(seg.duracionParoSeg)}`);
          if (Number(seg.piezasNoOkParo) > 0) {
            lines.push(`Piezas No OK: ${seg.piezasNoOkParo}`);
          }
          return lines.join("\n");
        }
        if (seg.isDelay) {
          lines.push("Atraso");
          if (seg.delayDurationTexto) {
            lines.push(`Duracion: ${seg.delayDurationTexto}`);
          }
          return lines.join("\n");
        }
        if (seg.isExtraTime) {
          lines.push("Tiempo Extra");
          if (seg.tiempoExtraTexto) {
            lines.push(`Tiempo Extra: ${seg.tiempoExtraTexto}`);
          }
        return lines.join("\n");
      }
      const estadoLabel = seg.estado || this.estadoToLabel(seg.estadoId);
      if (String(estadoLabel).toLowerCase() !== "muerto") {
        lines.push(estadoLabel);
      }
      const articuloLabel = this.buildSegmentArticuloLabel(seg);
      if (articuloLabel) {
        lines.push(`Artículo: ${articuloLabel}`);
      }
      if (seg.area) {
        lines.push(`Area: ${seg.area}`);
      }
      if (seg.planView === "real") {
        if (seg.maquinaAsignada) {
          lines.push(`Maquina Asignada: ${seg.maquinaAsignada}`);
        }
        if (seg.piezasReportadasTexto) {
          lines.push(`Piezas Reportadas: ${seg.piezasReportadasTexto}`);
        }
        return lines.join("\n");
      }
      if (seg.piezasPlaneadasTexto) {
        lines.push(`Piezas Planeadas: ${seg.piezasPlaneadasTexto}`);
      }
      if (seg.inicioBarraTexto) {
        lines.push(`Inicio: ${seg.inicioBarraTexto}`);
      }
      if (seg.finBarraTexto) {
        lines.push(`Fin: ${seg.finBarraTexto}`);
      }
      if (seg.finTexto) {
        lines.push(`Fin Teórico: ${seg.finTexto}`);
      }
      return lines.join("\n");
    },
    buildSegmentArticuloLabel(seg) {
      if (!seg) return "";
      const articulo = this.pickFirstText([seg.articulo]);
      const codigo = this.pickFirstText([seg.codigoArticulo]);
      if (articulo && codigo) return `${articulo} - [${codigo}]`;
      return articulo || codigo;
    },
    normalizeShift(shift) {
      if (!shift || typeof shift !== "object") return null;
      const start = this.minutesToTime(this.timeToMinutes(shift.start || shift.horarioInicio));
      const end = this.minutesToTime(this.timeToMinutes(shift.end || shift.horarioFin));
      if (!start || !end) return null;
      return {
        start,
        end,
        terminaDiaSiguiente: !!shift.terminaDiaSiguiente,
      };
    },
    minutesToTime(minutes) {
      const value = Number(minutes);
      if (!Number.isFinite(value)) return "";
      const normalized = ((Math.floor(value) % 1440) + 1440) % 1440;
      const hh = String(Math.floor(normalized / 60)).padStart(2, "0");
      const mm = String(normalized % 60).padStart(2, "0");
      return `${hh}:${mm}`;
    },
    extractTime(value) {
      if (!value && value !== 0) return "";
      let raw = String(value).trim();
      if (!raw) return "";
      if (raw.includes("T")) raw = raw.split("T")[1];
      if (raw.includes(" ")) {
        const pieces = raw.split(" ");
        raw = pieces[pieces.length - 1];
      }
      if (raw.includes(".")) raw = raw.split(".")[0];
      if (raw.endsWith("Z")) raw = raw.slice(0, -1);
      return raw;
    },
    toDateKey(value) {
      if (!value && value !== 0) return "";
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return this.formatDateOnly(value);
      }
      const text = String(value);
      const match = text.match(/^(\d{4}-\d{2}-\d{2})/);
      if (match && match[1]) return match[1];
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return "";
      return this.formatDateOnly(parsed);
    },
    dayNameFromKey(dayKey) {
      const date = new Date(`${dayKey}T00:00:00`);
      if (Number.isNaN(date.getTime())) return dayKey;
      switch (date.getDay()) {
        case 1:
          return "Lunes";
        case 2:
          return "Martes";
        case 3:
          return "Miercoles";
        case 4:
          return "Jueves";
        case 5:
          return "Viernes";
        case 6:
          return "Sabado";
        default:
          return "Domingo";
      }
    },
    timeToMinutes(time) {
      if (typeof time === "number") return time;
      const value = this.extractTime(time);
      if (!value || typeof value !== "string") return NaN;
      const parts = value.split(":");
      if (!parts.length) return NaN;
      const h = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      const secRaw = (parts[2] || "0").replace(",", ".");
      const s = parseFloat(secRaw);
      const hh = Number.isFinite(h) ? h : 0;
      const mm = Number.isFinite(m) ? m : 0;
      const ss = Number.isFinite(s) ? s : 0;
      return hh * 60 + mm + (ss / 60);
    },
    formatTimeLabel(minutes) {
      const total = Math.round(Number(minutes));
      if (!Number.isFinite(total)) return "--:--";
      const h24 = ((Math.floor(total / 60) % 24) + 24) % 24;
      const m = ((total % 60) + 60) % 60;
      const hh = String(h24).padStart(2, "0");
      if (m === 0) return hh;
      return `${hh}:${String(m).padStart(2, "0")}`;
    },
    buildHourTicks(startMin, endMin) {
      const ticks = [];
      let cursor = startMin;
      while (cursor <= endMin) {
        ticks.push(this.formatTimeLabel(cursor));
        cursor += 60;
      }
      const endLabel = this.formatTimeLabel(endMin);
      if (ticks[ticks.length - 1] !== endLabel) {
        ticks.push(endLabel);
      }
      return ticks;
    },
    hourGridStyle(day) {
      return {
        gridTemplateColumns: `repeat(${day.tickCount}, 1fr)`,
      };
    },
    dayGridStyle(day) {
      const hourSlots = Math.max(day.totalMin / 60, 1);
      const subSlots = Math.max(day.totalMin / 15, 4);
      return {
        "--hour-slots": hourSlots,
        "--sub-slots": subSlots,
      };
    },
    segmentsByDay(row, dayKey) {
      if (!row || !Array.isArray(row.segments)) return [];
      return row.segments.filter((seg) => seg.day === dayKey);
    },
    showNowLine(day) {
      return this.nowMarker && day.key === this.nowMarker.dayKey;
    },
    nowStyle(day) {
      const meta = this.dayMap[day.key];
      if (!meta) return {};
      const nowMin = this.timeToMinutes(this.nowMarker.time);
      const left = this.minutesToPercent(nowMin, meta);
      return {
        left: `${left}%`,
      };
    },
    minutesToPercent(min, meta) {
      if (!meta || meta.totalMin <= 0) return 0;
      const value = ((min - meta.startMin) / meta.totalMin) * 100;
      return Math.max(0, Math.min(100, value));
    },
    segmentStyle(seg, day) {
      const meta = this.dayMap[day.key];
      if (!meta) return {};
      const startMin = Number.isFinite(seg.startMin) ? seg.startMin : this.timeToMinutes(seg.start);
      let endMin = Number.isFinite(seg.endMin) ? seg.endMin : this.timeToMinutes(seg.end);
      if (Number.isFinite(startMin) && Number.isFinite(endMin) && endMin < startMin) {
        endMin += 24 * 60;
      }
      const clampedStart = Math.max(startMin, meta.startMin);
      const clampedEnd = Math.min(endMin, meta.endMin);
      if (!Number.isFinite(clampedStart) || !Number.isFinite(clampedEnd)) {
        return {};
      }
      if (clampedEnd <= clampedStart) {
        return { display: "none" };
      }
      const left = this.minutesToPercent(clampedStart, meta);
      const right = this.minutesToPercent(clampedEnd, meta);
      return {
        left: `${left}%`,
        width: `${Math.max(0, right - left)}%`,
        backgroundColor: this.typeColors[seg.type] || "#cbd5e1",
        border: seg.type === "muerto" ? "1px solid #8a5b3f" : "none",
        boxSizing: "border-box",
      };
    },
    segmentClasses(seg) {
      return {
        [`seg-${seg.type}`]: true,
        "segment--paro": !!(seg && seg.isParo),
        "segment--base-real": !!(seg && seg.planView === "real" && !seg.isParo),
      };
    },
    openDetalles(row, seg) {
      const selected = (seg && seg.odt) || row.odt;
      if (!selected) return;
      const fragmentoSeleccionado = Number(seg && seg.fragmentoNumero);
      const selectedParoId = Number(seg && seg.idParo);
      const selectedOperationId = Number(seg && seg.idOperacion);
      const selectedDelay = !!(seg && seg.isDelay);
      this.selectedOdt = {
        ...selected,
        fragmentoSeleccionado: Number.isFinite(fragmentoSeleccionado) && fragmentoSeleccionado > 0
          ? fragmentoSeleccionado
          : null,
        selectedParoId: Number.isFinite(selectedParoId) && selectedParoId > 0
          ? selectedParoId
          : null,
        selectedOperationId: Number.isFinite(selectedOperationId) && selectedOperationId > 0
          ? selectedOperationId
          : null,
        selectedDelay,
      };
      this.dialogOdt = true;
    },
  },
  mounted() {
    const defaultRange = this.defaultWeekRange();
    this.filters.inicio = defaultRange.inicio;
    this.filters.fin = defaultRange.fin;
    this.init();
  },
  watch: {
    selectedMachineKeys() {
      this.applyMachineFilter();
    },
  },
};
</script>

<style scoped>
.plan-produccion-card {
  border: 1px solid #e0e6ef;
  border-radius: 6px;
  background-color: #fff;
  overflow: hidden;
}

.plan-produccion {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.filters-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #e0e6ef;
  background-color: #fafbfe;
}

.filter-field {
  min-width: 170px;
  max-width: 210px;
}

.filter-field.filter-machines {
  min-width: 260px;
  max-width: 360px;
}

.filter-btn {
  align-self: stretch;
}

.view-switch {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px 0;
}

.view-toggle-btn {
  border: 1px solid #d7deea;
  background: #fff;
  color: #5b6475;
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.view-toggle-btn--active {
  background: #1f2937;
  border-color: #1f2937;
  color: #fff;
}

.plan-scroll {
  overflow-x: auto;
  overflow-y: hidden;
}

.plan-content {
  min-width: calc(var(--label-width) + var(--timeline-width));
}

.plan-header {
  background-color: #f4f6fb;
  border-bottom: 1px solid #e0e6ef;
}

.days-row {
  display: grid;
  grid-template-columns: var(--label-width) var(--timeline-width);
  align-items: center;
  border-bottom: 1px solid #e0e6ef;
}

.label-spacer {
  height: 100%;
}

.days-track-wrapper {
  position: relative;
  width: var(--timeline-width);
}

.days-track {
  display: grid;
}

.nav-btn {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 28px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #6b7280;
}

.nav-left {
  left: -28px;
}

.nav-right {
  right: -28px;
}

.day-pill {
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #7a8497;
  letter-spacing: 0.6px;
  padding: 8px 4px;
  border-left: 1px solid #e0e6ef;
}

.day-pill:last-child {
  border-right: 1px solid #e0e6ef;
}

.day-pill.active {
  background-color: #e8eef8;
  color: #2b2f36;
}

.time-row,
.legend-row {
  display: grid;
  grid-template-columns: var(--label-width) var(--timeline-width);
  align-items: center;
  border-bottom: 1px solid #e0e6ef;
}

.time-labels {
  display: grid;
}

.time-label {
  text-align: center;
}

.time-day {
  border-left: 1px solid #e0e6ef;
}

.time-day:last-child {
  border-right: 1px solid #e0e6ef;
}

.time-day-labels {
  display: grid;
  font-size: 11px;
  color: #7a8497;
  padding: 6px 8px;
}

.legend-items {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 6px 8px 8px;
  flex-wrap: wrap;
  width: var(--timeline-width);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #5b6475;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 2px;
}

.plan-body {
  max-height: 420px;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: #fff;
  width: calc(var(--label-width) + var(--timeline-width));
}

.plan-empty-state {
  padding: 28px 16px;
  text-align: center;
  color: #6b7280;
  font-size: 13px;
}

.plan-row {
  display: flex;
  min-height: 32px;
  border-bottom: 1px solid #e8edf5;
}

.row-timeline {
  position: relative;
  width: var(--timeline-width);
}

.time-day.active {
  background-color: #f4f7fb;
}

.row-day.active {
  background-color: #f8fafc;
}

.row-days {
  display: grid;
  height: 100%;
}

.row-day {
  position: relative;
  background-image: linear-gradient(to right, rgba(226, 231, 240, 0.55) 1px, transparent 1px),
    linear-gradient(to right, rgba(226, 231, 240, 0.2) 1px, transparent 1px);
  background-size: calc(100% / var(--hour-slots)) 100%,
    calc(100% / var(--sub-slots)) 100%;
  background-repeat: repeat;
  border-left: 1px solid rgba(232, 237, 245, 0.65);
}

.row-day:last-child {
  border-right: 1px solid rgba(232, 237, 245, 0.65);
}

.segments {
  position: relative;
  height: 100%;
}

.segment {
  position: absolute;
  height: 16px;
  top: 50%;
  transform: translateY(-50%);
  border-radius: 3px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.12);
  cursor: pointer;
}

.segment--base-real {
  height: 14px;
  top: 50%;
  z-index: 1;
}

.segment--paro {
  height: 14px;
  top: 50%;
  z-index: 3;
  border-radius: 3px;
}

.machine-block {
  border-bottom: 1px solid #e0e6ef;
}

.machine-title {
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: #334155;
  background-color: #eef3f8;
  border-bottom: 1px solid #d9e2ee;
  text-transform: uppercase;
}

.machine-rows {
  display: flex;
  flex-direction: column;
  width: calc(var(--mold-width) + var(--timeline-width));
}

.row-mold {
  width: var(--mold-width);
  display: block;
  padding: 6px 10px 4px;
  font-size: 12px;
  color: #5b6475;
  border-right: 1px solid #e8edf5;
  background-color: #f7f8fb;
}

.odt-detail {
  line-height: 1.3;
}

.odt-label {
  font-weight: 700;
  color: #374151;
}

.now-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background-color: #e53935;
  pointer-events: none;
  z-index: 2;
}

.seg-prod {
  background-color: #34a853;
}

.seg-wait {
  background-color: #42a5f5;
}

.seg-stop {
  background-color: #424242;
}

.seg-rework {
  background-color: #8d4b30;
}
</style>
